import { Effect, Schema } from "effect";
import { WorkspaceRoleSchema } from "./workspace";

/** Strict default for every untrusted Sandbox/R2 transport boundary. */
export const SandboxBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const SANDBOX_COMMAND_OUTPUT_MAX_LENGTH = 256 * 1_024;
export const SANDBOX_WORKER_REQUEST_MAX_BYTES = 256 * 1_024;
export const SANDBOX_WORKSPACE_FILE_LIST_DEFAULT_LIMIT = 100;
export const SANDBOX_WORKSPACE_FILE_LIST_MAX_LIMIT = 200;
export const SANDBOX_WORKSPACE_DIRECTORY_MAX_ENTRIES = 10_000;
export const SANDBOX_TEXT_FILE_READ_DEFAULT_MAX_BYTES = 256 * 1_024;
export const SANDBOX_TEXT_FILE_READ_MAX_BYTES = 256 * 1_024;
/** Durable admission ceilings charged from the requested maximum before Worker dispatch. */
export const SANDBOX_WORKSPACE_FILE_ACTOR_REQUESTS_PER_MINUTE = 30;
export const SANDBOX_WORKSPACE_FILE_WORKSPACE_REQUESTS_PER_MINUTE = 120;
export const SANDBOX_WORKSPACE_FILE_ACTOR_CAPACITY_UNITS_PER_MINUTE = 4_096;
export const SANDBOX_WORKSPACE_FILE_WORKSPACE_CAPACITY_UNITS_PER_MINUTE = 16_384;
export const SANDBOX_EXECUTION_GRANT_MAX_TTL_MS = 5 * 60 * 1_000;
export const SANDBOX_STAGE_A_NETWORK_POLICY_ID = "stage-a-none-v1";
export const SANDBOX_STAGE_A_CAPABILITIES = ["process", "filesystem", "backup"] as const;

/** Parses the exact, server-only Cloud workspace-file rollout boundary. Missing means deny all. */
export function parseSandboxWorkspaceFileCanaryWorkspaceIds(
  value: string | undefined,
): ReadonlySet<string> {
  if (value !== undefined && value.length > 8_192) {
    throw new Error("Sandbox workspace-file canary allowlist is too large");
  }
  const entries = value?.split(",") ?? [];
  const workspaceIds = new Set(entries.map((entry) => entry.trim()));
  if (
    entries.some((entry) => entry.trim() === "") ||
    [...workspaceIds].some(
      (workspaceId) => workspaceId.length > 128 || !/^[A-Za-z0-9_-]+$/u.test(workspaceId),
    )
  ) {
    throw new Error("Sandbox workspace-file canary workspace IDs are invalid");
  }
  return workspaceIds;
}

const hasAsciiControlCharacter = (value: string): boolean => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || codePoint === 0x7f) return true;
  }
  return false;
};

const hasAsciiControlOrSpace = (value: string): boolean => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x20 || codePoint === 0x7f) return true;
  }
  return false;
};

const CanonicalIdSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(128),
  Schema.makeFilter(
    (value) =>
      (value === value.trim() && !value.includes("\0")) ||
      "must be a canonical non-empty identifier",
  ),
);
const RequestIdSchema = CanonicalIdSchema.check(
  Schema.makeFilter(
    (value) =>
      !/^[A-Za-z0-9_-]{43}$/u.test(value) ||
      "must not use the reserved bearer-capability token shape",
  ),
).pipe(Schema.brand("RequestId"));
const WorkspaceIdSchema = CanonicalIdSchema.pipe(Schema.brand("WorkspaceId"));
const ProjectIdSchema = CanonicalIdSchema.pipe(Schema.brand("ProjectId"));
const EnvironmentIdSchema = CanonicalIdSchema.pipe(Schema.brand("EnvironmentId"));
const UserIdSchema = CanonicalIdSchema.pipe(Schema.brand("UserId"));
const ArtifactIdSchema = CanonicalIdSchema.pipe(Schema.brand("ArtifactId"));
const ArtifactRevisionIdSchema = CanonicalIdSchema.pipe(Schema.brand("ArtifactRevisionId"));
export const SandboxRequestIdSchema = RequestIdSchema;
export const SandboxWorkspaceIdSchema = WorkspaceIdSchema;
export const SandboxProjectIdSchema = ProjectIdSchema;
export const SandboxArtifactIdSchema = ArtifactIdSchema;
export const SandboxArtifactRevisionIdSchema = ArtifactRevisionIdSchema;

export const SandboxIdSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(128),
  Schema.makeFilter(
    (value) =>
      /^[A-Za-z0-9_-]+$/.test(value) ||
      "must contain only URL-safe server-derived sandbox ID characters",
  ),
).pipe(Schema.brand("SandboxId"));
export const SandboxProcessIdSchema = CanonicalIdSchema.pipe(Schema.brand("SandboxProcessId"));
export const SandboxTerminalIdSchema = CanonicalIdSchema.pipe(Schema.brand("SandboxTerminalId"));
export const SandboxBackupIdSchema = CanonicalIdSchema.pipe(Schema.brand("SandboxBackupId"));
export const ArtifactObjectIdSchema = CanonicalIdSchema.pipe(Schema.brand("ArtifactObjectId"));
export const UploadIdSchema = CanonicalIdSchema.pipe(Schema.brand("UploadId"));
export const SandboxGenerationSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(160),
  Schema.makeFilter(
    (value) =>
      (value === value.trim() && !value.includes("\0")) || "must be a canonical generation",
  ),
).pipe(Schema.brand("SandboxGeneration"));

const TrimmedTextSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.makeFilter((value) => value === value.trim() || "must not have surrounding whitespace"),
);
const IsoDateTimeSchema = Schema.String.check(
  Schema.isMaxLength(40),
  Schema.makeFilter(
    (value) =>
      (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/.test(value) &&
        Number.isFinite(globalThis.Date.parse(value))) ||
      "must be an ISO-8601 UTC timestamp",
  ),
);
const IdempotencyKeySchema = Schema.String.check(
  Schema.isMinLength(8),
  Schema.isMaxLength(160),
  Schema.makeFilter(
    (value) =>
      (value === value.trim() && !hasAsciiControlCharacter(value)) ||
      "must be a canonical idempotency key",
  ),
);
const PositiveSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(1),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
const NonNegativeSafeIntegerSchema = Schema.Int.check(
  Schema.isGreaterThanOrEqualTo(0),
  Schema.isLessThanOrEqualTo(Number.MAX_SAFE_INTEGER),
);
export const SandboxOperationInputDigestSchema = Schema.String.check(
  Schema.makeFilter(
    (value) => /^[a-f0-9]{64}$/.test(value) || "must be a lowercase SHA-256 hexadecimal value",
  ),
).pipe(Schema.brand("SandboxOperationInputDigest"));

const sandboxPathIssue = (
  value: string,
  allowedRoots: ReadonlyArray<"/workspace" | "/mnt/artifacts" | "/tmp">,
): true | string => {
  if (value.length === 0 || hasAsciiControlCharacter(value) || value.includes("\\")) {
    return "must be a canonical sandbox path without control characters";
  }
  if (/%(?:2e|2f|5c)/iu.test(value)) {
    return "must not contain percent-encoded traversal or separators";
  }
  const root = allowedRoots.find(
    (candidate) => value === candidate || value.startsWith(`${candidate}/`),
  );
  if (root === undefined) return "must stay inside an allowed sandbox root";
  const suffix = value.slice(root.length);
  if (
    suffix.endsWith("/") ||
    suffix
      .split("/")
      .some((segment, index) => index > 0 && (!segment || segment === "." || segment === ".."))
  ) {
    return "must be a canonical sandbox path without traversal";
  }
  return true;
};

export const SandboxPathSchema = Schema.String.check(
  Schema.isMaxLength(2_048),
  Schema.makeFilter((value) => sandboxPathIssue(value, ["/workspace", "/mnt/artifacts", "/tmp"])),
).pipe(Schema.brand("SandboxPath"));
export const SandboxWorkspacePathSchema = Schema.String.check(
  Schema.isMaxLength(2_048),
  Schema.makeFilter((value) => sandboxPathIssue(value, ["/workspace"])),
).pipe(Schema.brand("SandboxWorkspacePath"));

const absoluteUrlParts = (value: string): RegExpExecArray | null =>
  /^([a-z][a-z0-9+.-]*):\/\/([^/?#]+)(?:[/?#]|$)/i.exec(value);
const httpUrlIssue = (value: string): true | string => {
  const parts = absoluteUrlParts(value);
  if (parts === null || hasAsciiControlOrSpace(value)) return "must be a valid URL";
  if (!/^(?:http|https)$/i.test(parts[1] ?? "")) return "must use HTTP or HTTPS";
  return !(parts[2] ?? "").includes("@") || "must not contain URL userinfo";
};
const HttpUrlSchema = Schema.String.check(
  Schema.isMaxLength(8_192),
  Schema.makeFilter(httpUrlIssue),
);
const WebSocketUrlSchema = Schema.String.check(
  Schema.isMaxLength(2_048),
  Schema.makeFilter((value) => {
    const parts = absoluteUrlParts(value);
    if (parts === null || hasAsciiControlOrSpace(value)) {
      return "must be a valid WebSocket URL";
    }
    if (!/^(?:ws|wss)$/i.test(parts[1] ?? "")) return "must use WS or WSS";
    return !(parts[2] ?? "").includes("@") || "must not contain URL userinfo";
  }),
);
export const SandboxGitRepositoryUrlSchema = Schema.String.check(
  Schema.isMaxLength(2_048),
  Schema.makeFilter((value) => {
    const parts = absoluteUrlParts(value);
    if (parts === null || hasAsciiControlOrSpace(value)) {
      return "must be a valid Git repository URL";
    }
    if (!/^(?:http|https|ssh|git)$/i.test(parts[1] ?? "")) {
      return "must use an admitted Git transport";
    }
    return !(parts[2] ?? "").includes("@") || "must not contain URL userinfo";
  }),
);

const secretEnvironmentNamePattern =
  /(?:^|_)(?:API_?KEY|ACCESS_?KEY|AUTH(?:ORIZATION)?|COOKIE|CREDENTIALS?|DATABASE_URL|KEY|PRIVATE_?KEY|PASS(?:WORD|WD)?|SECRET|SESSION|TOKEN)(?:_|$)/;
const reservedEnvironmentNamePattern =
  /^(?:BASH_ENV|BUN_OPTIONS|ENV|HOME|LD_LIBRARY_PATH|LD_PRELOAD|NODE_OPTIONS|PATH|SHELL)$|^DYLD_/;

const sandboxEnvironmentNameIssue = (value: string): true | string =>
  (/^[A-Z_][A-Z0-9_]*$/.test(value) &&
    value.length <= 96 &&
    !secretEnvironmentNamePattern.test(value) &&
    !reservedEnvironmentNamePattern.test(value)) ||
  "environment names must be uppercase, non-secret, and safe for the runtime";

export const SandboxEnvironmentNameSchema = Schema.String.check(
  Schema.makeFilter(sandboxEnvironmentNameIssue),
);
export const SandboxEnvironmentValueSchema = Schema.String.check(
  Schema.isMaxLength(8_192),
  Schema.makeFilter(
    (value) =>
      (!value.includes("\0") && !value.includes("\r") && !value.includes("\n")) ||
      "environment values must be single-line text",
  ),
);
export const SandboxEnvironmentSchema = Schema.Record(
  Schema.String,
  SandboxEnvironmentValueSchema,
).check(
  Schema.makeFilter(
    (environment) =>
      Object.keys(environment).every((name) => sandboxEnvironmentNameIssue(name) === true) ||
      "environment names must be uppercase, non-secret, and safe for the runtime",
  ),
  Schema.makeFilter(
    (environment) => Object.keys(environment).length <= 64 || "must contain at most 64 entries",
  ),
);

const R2StorageKeySchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(2_048),
  Schema.makeFilter((value) => {
    if (value.startsWith("/") || hasAsciiControlCharacter(value) || value.includes("\\")) {
      return "must be a canonical private object key";
    }
    return (
      value.split("/").every((segment) => segment && segment !== "." && segment !== "..") ||
      "must not contain empty or traversal segments"
    );
  }),
);

/** Execution targets belong to an active project, never to Developer Console. */
export const ExecutionTargetSchema = Schema.Literals(["local", "cloud-sandbox"]);
export const SandboxPurposeSchema = Schema.Literals([
  "interactive",
  "agent-task",
  "preview",
  "document-render",
  "artifact-export",
  "code-interpreter",
  "file-inspection",
]);
export const SandboxScopeSchema = Schema.Literals([
  "user-project",
  "project-task",
  "ephemeral-run",
]);
export const SandboxResourceProfileSchema = Schema.Literals(["standard", "performance"]);
export const SandboxStatusSchema = Schema.Literals([
  "queued",
  "starting",
  "restoring",
  "ready",
  "running",
  "sleeping",
  "stopped",
  "destroyed",
  "error",
]);
export const SandboxPersistenceSchema = Schema.Literals([
  "ephemeral",
  "checkpoint-pending",
  "checkpointed",
  "restore-required",
  "restoring",
  "error",
]);

export const SandboxReferenceSchema = Schema.Struct({
  id: SandboxIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  environmentId: Schema.optionalKey(EnvironmentIdSchema),
  ownerUserId: Schema.optionalKey(UserIdSchema),
  runId: Schema.optionalKey(CanonicalIdSchema),
  purpose: SandboxPurposeSchema,
  scope: SandboxScopeSchema,
  resourceProfile: SandboxResourceProfileSchema,
  status: SandboxStatusSchema,
  persistence: SandboxPersistenceSchema,
  generation: Schema.optionalKey(SandboxGenerationSchema),
  createdAt: IsoDateTimeSchema,
  lastActiveAt: IsoDateTimeSchema,
});

const GitRefSchema = Schema.String.check(
  Schema.isMinLength(1),
  Schema.isMaxLength(240),
  Schema.makeFilter(
    (value) =>
      (value === value.trim() &&
        !value.startsWith("-") &&
        !hasAsciiControlOrSpace(value) &&
        !["~", "^", ":", "?", "*", "[", "\\"].some((character) => value.includes(character)) &&
        !value.includes("..") &&
        !value.includes("@{")) ||
      "must be a canonical Git ref",
  ),
);
export const SandboxSeedSchema = Schema.Union([
  Schema.Struct({ type: Schema.Literal("empty") }),
  Schema.Struct({
    type: Schema.Literal("git"),
    repositoryUrl: SandboxGitRepositoryUrlSchema,
    ref: GitRefSchema,
  }),
  Schema.Struct({
    type: Schema.Literal("backup"),
    backupId: SandboxBackupIdSchema,
  }),
]);

export const SandboxCapabilitySchema = Schema.Literals([
  "process",
  "terminal",
  "filesystem",
  "preview",
  "git",
  "network",
  "artifact-read",
  "artifact-write",
  "backup",
]);
export const SandboxPermissionSchema = Schema.Literals([
  "sandbox.use",
  "sandbox.terminal",
  "sandbox.files.read",
  "sandbox.files.write",
  "sandbox.preview",
  "sandbox.network.request",
  "sandbox.checkpoint",
  "sandbox.restore",
  "sandbox.destroy",
]);
const SandboxExistingOperationSchema = Schema.Literals([
  "sandbox.process.execute",
  "sandbox.process.stop",
  "sandbox.terminal.create",
  "sandbox.events.stream",
  "sandbox.files.list",
  "sandbox.files.read",
  "sandbox.files.write",
  "sandbox.preview.expose",
  "sandbox.checkpoint.create",
  "sandbox.checkpoint.restore",
  "sandbox.destroy",
]);
type SandboxExistingOperation = typeof SandboxExistingOperationSchema.Type;
export const SandboxOperationSchema = Schema.Union([
  Schema.Literal("sandbox.ensure"),
  SandboxExistingOperationSchema,
]);

const executionGrantLifetimeIssue = (grant: {
  readonly notBefore: string;
  readonly expiresAt: string;
}): true | string => {
  const notBefore = globalThis.Date.parse(grant.notBefore);
  const expiresAt = globalThis.Date.parse(grant.expiresAt);
  return (
    (expiresAt > notBefore && expiresAt - notBefore <= SANDBOX_EXECUTION_GRANT_MAX_TTL_MS) ||
    "execution grant lifetime must be positive and at most five minutes"
  );
};

const ArgvValueSchema = Schema.String.check(
  Schema.isMaxLength(4_096),
  Schema.makeFilter((value) => !value.includes("\0") || "arguments must not contain NUL"),
);
export const SandboxCredentialProxyReferenceSchema = Schema.Struct({
  id: CanonicalIdSchema.pipe(Schema.brand("SandboxCredentialProxyReferenceId")),
  audience: TrimmedTextSchema.check(Schema.isMaxLength(255)),
  expiresAt: IsoDateTimeSchema,
  maximumUses: Schema.Literal(1),
});

export const SandboxCommandConstraintsSchema = Schema.Struct({
  argv: Schema.Array(ArgvValueSchema).check(Schema.isMinLength(1), Schema.isMaxLength(128)),
  cwd: SandboxPathSchema,
  env: SandboxEnvironmentSchema,
  credentialProxyReferences: Schema.Array(SandboxCredentialProxyReferenceSchema).check(
    Schema.isMaxLength(16),
  ),
  timeoutMs: PositiveSafeIntegerSchema.check(Schema.isLessThanOrEqualTo(3_600_000)),
  allowShell: Schema.Boolean,
});
export const SandboxProcessStopConstraintsSchema = Schema.Struct({
  processId: SandboxProcessIdSchema,
  signal: Schema.Literals(["SIGINT", "SIGTERM", "SIGKILL"]),
});
export const SandboxTerminalConstraintsSchema = Schema.Struct({
  cwd: SandboxPathSchema,
  cols: Schema.Int.check(Schema.isGreaterThanOrEqualTo(20), Schema.isLessThanOrEqualTo(500)),
  rows: Schema.Int.check(Schema.isGreaterThanOrEqualTo(5), Schema.isLessThanOrEqualTo(300)),
});
export const SandboxEventStreamConstraintsSchema = Schema.Struct({
  afterSequence: Schema.optionalKey(NonNegativeSafeIntegerSchema),
});
export const SandboxListFilesConstraintsSchema = Schema.Struct({
  path: SandboxWorkspacePathSchema,
  cursor: Schema.optionalKey(SandboxWorkspacePathSchema),
  limit: PositiveSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(SANDBOX_WORKSPACE_FILE_LIST_MAX_LIMIT),
  ),
});
export const SandboxReadTextFileConstraintsSchema = Schema.Struct({
  path: SandboxWorkspacePathSchema,
  maxBytes: PositiveSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(SANDBOX_TEXT_FILE_READ_MAX_BYTES),
  ),
});
export const SandboxWriteTextFileConstraintsSchema = Schema.Struct({
  path: SandboxWorkspacePathSchema,
  content: Schema.String.check(Schema.isMaxLength(10 * 1_024 * 1_024)),
  expectedRevision: Schema.optionalKey(TrimmedTextSchema.check(Schema.isMaxLength(256))),
});
/** Public preview is intentionally absent from the initial executable contract. */
export const SandboxPreviewAccessSchema = Schema.Literals(["private-ticket", "workspace"]);
export const SandboxPreviewConstraintsSchema = Schema.Struct({
  port: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1_024), Schema.isLessThanOrEqualTo(65_535)),
  access: SandboxPreviewAccessSchema,
});
export const SandboxCheckpointConstraintsSchema = Schema.Struct({
  name: TrimmedTextSchema.check(Schema.isMaxLength(256)),
  ttlSeconds: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(300),
    Schema.isLessThanOrEqualTo(30 * 24 * 60 * 60),
  ),
  useGitignore: Schema.Boolean,
});
export const SandboxRestoreConstraintsSchema = Schema.Struct({
  backupId: SandboxBackupIdSchema,
});
export const SandboxDestroyConstraintsSchema = Schema.Struct({
  reason: TrimmedTextSchema.check(Schema.isMaxLength(500)),
});

const sandboxCommandConstraintsEqual = Schema.toEquivalence(SandboxCommandConstraintsSchema);
const sandboxProcessStopConstraintsEqual = Schema.toEquivalence(
  SandboxProcessStopConstraintsSchema,
);
const sandboxTerminalConstraintsEqual = Schema.toEquivalence(SandboxTerminalConstraintsSchema);
const sandboxEventStreamConstraintsEqual = Schema.toEquivalence(
  SandboxEventStreamConstraintsSchema,
);
const sandboxListFilesConstraintsEqual = Schema.toEquivalence(SandboxListFilesConstraintsSchema);
const sandboxReadTextFileConstraintsEqual = Schema.toEquivalence(
  SandboxReadTextFileConstraintsSchema,
);
const sandboxWriteTextFileConstraintsEqual = Schema.toEquivalence(
  SandboxWriteTextFileConstraintsSchema,
);
const sandboxPreviewConstraintsEqual = Schema.toEquivalence(SandboxPreviewConstraintsSchema);
const sandboxCheckpointConstraintsEqual = Schema.toEquivalence(SandboxCheckpointConstraintsSchema);
const sandboxRestoreConstraintsEqual = Schema.toEquivalence(SandboxRestoreConstraintsSchema);
const sandboxDestroyConstraintsEqual = Schema.toEquivalence(SandboxDestroyConstraintsSchema);

const SandboxExecutionGrantFields = {
  jti: CanonicalIdSchema,
  issuer: TrimmedTextSchema.check(Schema.isMaxLength(160)),
  audience: Schema.Literal("papersflow-sandbox-worker"),
  actorId: CanonicalIdSchema,
  subjectId: CanonicalIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  runId: CanonicalIdSchema,
  nodeId: CanonicalIdSchema,
  attemptId: CanonicalIdSchema,
  attemptGeneration: PositiveSafeIntegerSchema,
  operationId: CanonicalIdSchema,
  toolCallId: CanonicalIdSchema,
  inputDigest: SandboxOperationInputDigestSchema,
  executionClaimGeneration: PositiveSafeIntegerSchema,
  capabilityVersion: PositiveSafeIntegerSchema,
  executionTarget: Schema.Literal("cloud-sandbox"),
  sandboxId: SandboxIdSchema,
  expectedGeneration: SandboxGenerationSchema,
  authorizationRevision: PositiveSafeIntegerSchema,
  policyRevision: PositiveSafeIntegerSchema,
  resourceProfile: SandboxResourceProfileSchema,
  networkPolicyId: CanonicalIdSchema,
  idempotencyKey: IdempotencyKeySchema,
  notBefore: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  maximumUses: Schema.Literal(1),
  requestId: RequestIdSchema,
} as const;

const exactPermissionSchema = (permission: SandboxPermission) =>
  Schema.Array(SandboxPermissionSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(1),
    Schema.makeFilter(
      (permissions) =>
        permissions[0] === permission || `grant admits only the ${permission} permission`,
    ),
  );

const existingExecutionGrantVariant = <
  const Operation extends SandboxExistingOperation,
  const Capability extends SandboxCapability,
  Constraints extends Schema.Top,
>(
  operation: Operation,
  capabilityId: Capability,
  permission: SandboxPermission,
  constraints: Constraints,
) =>
  Schema.Struct({
    ...SandboxExecutionGrantFields,
    operation: Schema.Literal(operation),
    capabilityId: Schema.Literal(capabilityId),
    permissions: exactPermissionSchema(permission),
    constraints,
  });

/**
 * Signed claims are short-lived, cloud-only, exact-operation authority. Each variant carries the
 * decoded normalized operation input, so the Worker never authorizes mutable request fields from a
 * separately reusable grant. Grants are ephemeral and must never become durable UI data.
 */
export const SandboxExecutionGrantSchema = Schema.Union([
  existingExecutionGrantVariant(
    "sandbox.process.execute",
    "process",
    "sandbox.use",
    SandboxCommandConstraintsSchema,
  ),
  existingExecutionGrantVariant(
    "sandbox.process.stop",
    "process",
    "sandbox.use",
    SandboxProcessStopConstraintsSchema,
  ),
  existingExecutionGrantVariant(
    "sandbox.terminal.create",
    "terminal",
    "sandbox.terminal",
    SandboxTerminalConstraintsSchema,
  ),
  existingExecutionGrantVariant(
    "sandbox.events.stream",
    "process",
    "sandbox.use",
    SandboxEventStreamConstraintsSchema,
  ),
  existingExecutionGrantVariant(
    "sandbox.files.list",
    "filesystem",
    "sandbox.files.read",
    SandboxListFilesConstraintsSchema,
  ),
  existingExecutionGrantVariant(
    "sandbox.files.read",
    "filesystem",
    "sandbox.files.read",
    SandboxReadTextFileConstraintsSchema,
  ),
  existingExecutionGrantVariant(
    "sandbox.files.write",
    "filesystem",
    "sandbox.files.write",
    SandboxWriteTextFileConstraintsSchema,
  ),
  existingExecutionGrantVariant(
    "sandbox.preview.expose",
    "preview",
    "sandbox.preview",
    SandboxPreviewConstraintsSchema,
  ),
  existingExecutionGrantVariant(
    "sandbox.checkpoint.create",
    "backup",
    "sandbox.checkpoint",
    SandboxCheckpointConstraintsSchema,
  ),
  existingExecutionGrantVariant(
    "sandbox.checkpoint.restore",
    "backup",
    "sandbox.restore",
    SandboxRestoreConstraintsSchema,
  ),
  existingExecutionGrantVariant(
    "sandbox.destroy",
    "process",
    "sandbox.destroy",
    SandboxDestroyConstraintsSchema,
  ),
]).check(Schema.makeFilter(executionGrantLifetimeIssue));

type ExistingSandboxRequestBinding = {
  readonly requestId: string;
  readonly workspaceId: string;
  readonly projectId: string;
  readonly sandboxId: string;
  readonly expectedGeneration: string;
  readonly idempotencyKey: string;
  readonly executionGrant: {
    readonly requestId: string;
    readonly workspaceId: string;
    readonly projectId: string;
    readonly sandboxId: string;
    readonly expectedGeneration: string;
    readonly idempotencyKey: string;
    readonly operation: SandboxOperation;
    readonly capabilityId: SandboxCapability;
    readonly permissions: ReadonlyArray<SandboxPermission>;
  };
};

const exactExecutionGrantIssue = (
  request: ExistingSandboxRequestBinding,
  capability: SandboxCapability,
  permission: SandboxPermission,
  operationAndInputMatch: boolean,
): true | string =>
  (request.requestId === request.executionGrant.requestId &&
    request.workspaceId === request.executionGrant.workspaceId &&
    request.projectId === request.executionGrant.projectId &&
    request.sandboxId === request.executionGrant.sandboxId &&
    request.expectedGeneration === request.executionGrant.expectedGeneration &&
    request.idempotencyKey === request.executionGrant.idempotencyKey &&
    request.executionGrant.capabilityId === capability &&
    request.executionGrant.permissions.length === 1 &&
    request.executionGrant.permissions[0] === permission &&
    operationAndInputMatch) ||
  "execution grant must bind the exact request, scope, generation, operation, permission, idempotency, and normalized input";

const RequestedSandboxCapabilitiesSchema = Schema.Array(SandboxCapabilitySchema).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(16),
  Schema.makeFilter(
    (capabilities) =>
      new Set(capabilities).size === capabilities.length ||
      "requested sandbox capabilities must be unique",
  ),
);

/**
 * Ensure has no existing runtime generation to fence. Its dedicated signed grant instead binds the
 * exact logical start/wake input and immutable run operation that is allowed to create one.
 */
export const SandboxEnsureExecutionGrantSchema = Schema.Struct({
  jti: CanonicalIdSchema,
  issuer: TrimmedTextSchema.check(Schema.isMaxLength(160)),
  audience: Schema.Literal("papersflow-sandbox-worker"),
  actorId: CanonicalIdSchema,
  subjectId: CanonicalIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  environmentId: Schema.optionalKey(EnvironmentIdSchema),
  ownerUserId: Schema.optionalKey(UserIdSchema),
  runId: CanonicalIdSchema,
  nodeId: CanonicalIdSchema,
  attemptId: CanonicalIdSchema,
  attemptGeneration: PositiveSafeIntegerSchema,
  operationId: CanonicalIdSchema,
  toolCallId: CanonicalIdSchema,
  inputDigest: SandboxOperationInputDigestSchema,
  executionClaimGeneration: PositiveSafeIntegerSchema,
  capabilityId: Schema.Literal("sandbox.ensure"),
  capabilityVersion: PositiveSafeIntegerSchema,
  operation: Schema.Literal("sandbox.ensure"),
  executionTarget: Schema.Literal("cloud-sandbox"),
  authorizationRevision: PositiveSafeIntegerSchema,
  policyRevision: PositiveSafeIntegerSchema,
  permissions: Schema.Array(SandboxPermissionSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(1),
    Schema.makeFilter(
      (permissions) => permissions[0] === "sandbox.use" || "ensure grants admit only sandbox.use",
    ),
  ),
  resourceProfile: SandboxResourceProfileSchema,
  networkPolicyId: CanonicalIdSchema,
  purpose: SandboxPurposeSchema,
  scope: SandboxScopeSchema,
  seed: SandboxSeedSchema,
  requestedCapabilities: RequestedSandboxCapabilitiesSchema,
  idempotencyKey: IdempotencyKeySchema,
  notBefore: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  maximumUses: Schema.Literal(1),
  requestId: RequestIdSchema,
  sandboxId: Schema.optionalKey(SandboxIdSchema),
}).check(Schema.makeFilter(executionGrantLifetimeIssue));

const sandboxSeedsEqual = (left: SandboxSeed, right: SandboxSeed): boolean => {
  if (left.type !== right.type) return false;
  if (left.type === "empty" && right.type === "empty") return true;
  if (left.type === "git" && right.type === "git") {
    return left.repositoryUrl === right.repositoryUrl && left.ref === right.ref;
  }
  return left.type === "backup" && right.type === "backup" && left.backupId === right.backupId;
};

const sandboxCapabilitiesEqual = (
  left: ReadonlyArray<SandboxCapability>,
  right: ReadonlyArray<SandboxCapability>,
): boolean =>
  left.length === right.length && left.every((capability, index) => capability === right[index]);

export const EnsureSandboxRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  idempotencyKey: IdempotencyKeySchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  environmentId: Schema.optionalKey(EnvironmentIdSchema),
  ownerUserId: Schema.optionalKey(UserIdSchema),
  sandboxId: Schema.optionalKey(SandboxIdSchema),
  runId: CanonicalIdSchema,
  purpose: SandboxPurposeSchema,
  scope: SandboxScopeSchema,
  resourceProfile: SandboxResourceProfileSchema,
  seed: SandboxSeedSchema,
  requestedCapabilities: RequestedSandboxCapabilitiesSchema,
  executionGrant: SandboxEnsureExecutionGrantSchema,
}).check(
  Schema.makeFilter(
    (request) =>
      ((request.scope !== "user-project" ||
        (request.ownerUserId !== undefined &&
          request.ownerUserId === request.executionGrant.actorId)) &&
        request.requestId === request.executionGrant.requestId &&
        request.idempotencyKey === request.executionGrant.idempotencyKey &&
        request.workspaceId === request.executionGrant.workspaceId &&
        request.projectId === request.executionGrant.projectId &&
        request.environmentId === request.executionGrant.environmentId &&
        request.ownerUserId === request.executionGrant.ownerUserId &&
        request.sandboxId === request.executionGrant.sandboxId &&
        request.runId === request.executionGrant.runId &&
        request.purpose === request.executionGrant.purpose &&
        request.scope === request.executionGrant.scope &&
        request.resourceProfile === request.executionGrant.resourceProfile &&
        sandboxSeedsEqual(request.seed, request.executionGrant.seed) &&
        sandboxCapabilitiesEqual(
          request.requestedCapabilities,
          request.executionGrant.requestedCapabilities,
        )) ||
      "ensure execution grant must bind the exact request, run, scope, and normalized input",
  ),
);

const SandboxTargetRequestFields = {
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  sandboxId: SandboxIdSchema,
  expectedGeneration: SandboxGenerationSchema,
} as const;

export const SandboxCommandRequestSchema = Schema.Struct({
  ...SandboxTargetRequestFields,
  executionGrant: SandboxExecutionGrantSchema,
  argv: Schema.Array(ArgvValueSchema).check(Schema.isMinLength(1), Schema.isMaxLength(128)),
  cwd: SandboxPathSchema,
  env: SandboxEnvironmentSchema,
  credentialProxyReferences: Schema.Array(SandboxCredentialProxyReferenceSchema).check(
    Schema.isMaxLength(16),
  ),
  timeoutMs: PositiveSafeIntegerSchema.check(Schema.isLessThanOrEqualTo(3_600_000)),
  idempotencyKey: IdempotencyKeySchema,
  allowShell: Schema.Boolean,
}).check(
  Schema.makeFilter((request) => {
    const grant = request.executionGrant;
    return exactExecutionGrantIssue(
      request,
      "process",
      "sandbox.use",
      grant.operation === "sandbox.process.execute" &&
        sandboxCommandConstraintsEqual(
          {
            argv: request.argv,
            cwd: request.cwd,
            env: request.env,
            credentialProxyReferences: request.credentialProxyReferences,
            timeoutMs: request.timeoutMs,
            allowShell: request.allowShell,
          },
          grant.constraints,
        ),
    );
  }),
);

export const SandboxProcessStopRequestSchema = Schema.Struct({
  ...SandboxTargetRequestFields,
  executionGrant: SandboxExecutionGrantSchema,
  processId: SandboxProcessIdSchema,
  signal: Schema.Literals(["SIGINT", "SIGTERM", "SIGKILL"]),
  idempotencyKey: IdempotencyKeySchema,
}).check(
  Schema.makeFilter((request) => {
    const grant = request.executionGrant;
    return exactExecutionGrantIssue(
      request,
      "process",
      "sandbox.use",
      grant.operation === "sandbox.process.stop" &&
        sandboxProcessStopConstraintsEqual(
          { processId: request.processId, signal: request.signal },
          grant.constraints,
        ),
    );
  }),
);

export const SandboxTerminalRequestSchema = Schema.Struct({
  ...SandboxTargetRequestFields,
  executionGrant: SandboxExecutionGrantSchema,
  cwd: SandboxPathSchema,
  cols: Schema.Int.check(Schema.isGreaterThanOrEqualTo(20), Schema.isLessThanOrEqualTo(500)),
  rows: Schema.Int.check(Schema.isGreaterThanOrEqualTo(5), Schema.isLessThanOrEqualTo(300)),
  idempotencyKey: IdempotencyKeySchema,
}).check(
  Schema.makeFilter((request) => {
    const grant = request.executionGrant;
    return exactExecutionGrantIssue(
      request,
      "terminal",
      "sandbox.terminal",
      grant.operation === "sandbox.terminal.create" &&
        sandboxTerminalConstraintsEqual(
          { cwd: request.cwd, cols: request.cols, rows: request.rows },
          grant.constraints,
        ),
    );
  }),
);

export const SandboxEventStreamRequestSchema = Schema.Struct({
  ...SandboxTargetRequestFields,
  executionGrant: SandboxExecutionGrantSchema,
  afterSequence: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  idempotencyKey: IdempotencyKeySchema,
}).check(
  Schema.makeFilter((request) => {
    const grant = request.executionGrant;
    return exactExecutionGrantIssue(
      request,
      "process",
      "sandbox.use",
      grant.operation === "sandbox.events.stream" &&
        sandboxEventStreamConstraintsEqual(
          request.afterSequence !== undefined ? { afterSequence: request.afterSequence } : {},
          grant.constraints,
        ),
    );
  }),
);

export const SandboxListFilesRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  sandboxId: SandboxIdSchema,
  // This is the browser's observed generation, not authorization authority. Convex must compare
  // it with the current sandbox row before the Agent API can mint a Worker grant.
  expectedGeneration: SandboxGenerationSchema,
  path: SandboxWorkspacePathSchema,
  cursor: Schema.optionalKey(SandboxWorkspacePathSchema),
  limit: PositiveSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(SANDBOX_WORKSPACE_FILE_LIST_MAX_LIMIT),
  ),
});

export const SandboxReadTextFileRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  sandboxId: SandboxIdSchema,
  expectedGeneration: SandboxGenerationSchema,
  path: SandboxWorkspacePathSchema,
  maxBytes: PositiveSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(SANDBOX_TEXT_FILE_READ_MAX_BYTES),
  ),
});

export const SandboxWorkspaceFileOperationSchema = Schema.Literals([
  "sandbox.files.list",
  "sandbox.files.read",
]);

const SandboxWorkspaceFileAuthorizationTargetFields = {
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  sandboxId: SandboxIdSchema,
  expectedGeneration: SandboxGenerationSchema,
} as const;

export const SandboxAuthorizeWorkspaceFileReadRequestSchema = Schema.Union([
  Schema.Struct({
    ...SandboxWorkspaceFileAuthorizationTargetFields,
    operation: Schema.Literal("sandbox.files.list"),
    limit: PositiveSafeIntegerSchema.check(
      Schema.isLessThanOrEqualTo(SANDBOX_WORKSPACE_FILE_LIST_MAX_LIMIT),
    ),
  }),
  Schema.Struct({
    ...SandboxWorkspaceFileAuthorizationTargetFields,
    operation: Schema.Literal("sandbox.files.read"),
    maxBytes: PositiveSafeIntegerSchema.check(
      Schema.isLessThanOrEqualTo(SANDBOX_TEXT_FILE_READ_MAX_BYTES),
    ),
  }),
]);

export const SandboxDiscoverReadableRuntimesRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
});

export const SandboxReadableRuntimeSchema = Schema.Struct({
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
  status: Schema.Literals(["ready", "running"]),
  purpose: SandboxPurposeSchema,
  scope: SandboxScopeSchema,
  runId: CanonicalIdSchema,
  threadId: CanonicalIdSchema,
  lastActiveAt: IsoDateTimeSchema,
});

export const SandboxDiscoverReadableRuntimesResultSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  runtimes: Schema.Array(SandboxReadableRuntimeSchema).check(Schema.isMaxLength(100)),
  truncated: Schema.Boolean,
});

export const SandboxWorkspaceFileAuthoritySchema = Schema.Struct({
  userId: UserIdSchema,
  role: WorkspaceRoleSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
});

export const SandboxAuthorizedListFilesRequestSchema = SandboxListFilesRequestSchema;

export const SandboxAuthorizedReadTextFileRequestSchema = SandboxReadTextFileRequestSchema;

const SandboxWorkspaceFileExecutionGrantFields = {
  jti: CanonicalIdSchema,
  issuer: TrimmedTextSchema.check(Schema.isMaxLength(160)),
  audience: Schema.Literal("papersflow-sandbox-worker"),
  actorId: UserIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  requestId: RequestIdSchema,
  capabilityId: Schema.Literal("filesystem"),
  capabilityVersion: PositiveSafeIntegerSchema,
  executionTarget: Schema.Literal("cloud-sandbox"),
  sandboxId: SandboxIdSchema,
  expectedGeneration: SandboxGenerationSchema,
  authorizationRevision: PositiveSafeIntegerSchema,
  policyRevision: PositiveSafeIntegerSchema,
  resourceProfile: Schema.Literal("standard"),
  networkPolicyId: CanonicalIdSchema,
  idempotencyKey: IdempotencyKeySchema,
  inputDigest: SandboxOperationInputDigestSchema,
  notBefore: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  maximumUses: Schema.Literal(1),
} as const;

const SandboxWorkspaceFileExecutionGrantSchema = Schema.Union([
  Schema.Struct({
    ...SandboxWorkspaceFileExecutionGrantFields,
    operation: Schema.Literal("sandbox.files.list"),
    permissions: exactPermissionSchema("sandbox.files.read"),
    constraints: SandboxListFilesConstraintsSchema,
  }),
  Schema.Struct({
    ...SandboxWorkspaceFileExecutionGrantFields,
    operation: Schema.Literal("sandbox.files.read"),
    permissions: exactPermissionSchema("sandbox.files.read"),
    constraints: SandboxReadTextFileConstraintsSchema,
  }),
]).check(Schema.makeFilter(executionGrantLifetimeIssue));

const workspaceFileGrantBindingIssue = (
  request: {
    readonly requestId: string;
    readonly workspaceId: string;
    readonly projectId: string;
    readonly sandboxId: string;
    readonly expectedGeneration: string;
    readonly idempotencyKey: string;
    readonly executionGrant: typeof SandboxWorkspaceFileExecutionGrantSchema.Type;
  },
  operationAndInputMatch: boolean,
): true | string =>
  (request.requestId === request.executionGrant.requestId &&
    request.workspaceId === request.executionGrant.workspaceId &&
    request.projectId === request.executionGrant.projectId &&
    request.sandboxId === request.executionGrant.sandboxId &&
    request.expectedGeneration === request.executionGrant.expectedGeneration &&
    request.idempotencyKey === request.executionGrant.idempotencyKey &&
    operationAndInputMatch) ||
  "workspace file grant must bind the exact request, target generation, and normalized input";

export const SandboxWorkerListFilesRequestSchema = Schema.Struct({
  ...SandboxAuthorizedListFilesRequestSchema.fields,
  idempotencyKey: IdempotencyKeySchema,
  executionGrant: SandboxWorkspaceFileExecutionGrantSchema,
}).check(
  Schema.makeFilter((request) =>
    workspaceFileGrantBindingIssue(
      request,
      request.executionGrant.operation === "sandbox.files.list" &&
        sandboxListFilesConstraintsEqual(
          {
            path: request.path,
            ...(request.cursor === undefined ? {} : { cursor: request.cursor }),
            limit: request.limit,
          },
          request.executionGrant.constraints,
        ),
    ),
  ),
);

export const SandboxWorkerReadTextFileRequestSchema = Schema.Struct({
  ...SandboxAuthorizedReadTextFileRequestSchema.fields,
  idempotencyKey: IdempotencyKeySchema,
  executionGrant: SandboxWorkspaceFileExecutionGrantSchema,
}).check(
  Schema.makeFilter((request) =>
    workspaceFileGrantBindingIssue(
      request,
      request.executionGrant.operation === "sandbox.files.read" &&
        sandboxReadTextFileConstraintsEqual(
          { path: request.path, maxBytes: request.maxBytes },
          request.executionGrant.constraints,
        ),
    ),
  ),
);

export const SandboxWriteTextFileRequestSchema = Schema.Struct({
  ...SandboxTargetRequestFields,
  executionGrant: SandboxExecutionGrantSchema,
  path: SandboxWorkspacePathSchema,
  content: Schema.String.check(Schema.isMaxLength(10 * 1_024 * 1_024)),
  expectedRevision: Schema.optionalKey(TrimmedTextSchema.check(Schema.isMaxLength(256))),
  idempotencyKey: IdempotencyKeySchema,
}).check(
  Schema.makeFilter((request) => {
    const grant = request.executionGrant;
    return exactExecutionGrantIssue(
      request,
      "filesystem",
      "sandbox.files.write",
      grant.operation === "sandbox.files.write" &&
        sandboxWriteTextFileConstraintsEqual(
          {
            path: request.path,
            content: request.content,
            ...(request.expectedRevision !== undefined
              ? { expectedRevision: request.expectedRevision }
              : {}),
          },
          grant.constraints,
        ),
    );
  }),
);

export const SandboxPreviewRequestSchema = Schema.Struct({
  ...SandboxTargetRequestFields,
  executionGrant: SandboxExecutionGrantSchema,
  port: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1_024), Schema.isLessThanOrEqualTo(65_535)),
  access: SandboxPreviewAccessSchema,
  idempotencyKey: IdempotencyKeySchema,
}).check(
  Schema.makeFilter((request) => {
    const grant = request.executionGrant;
    return exactExecutionGrantIssue(
      request,
      "preview",
      "sandbox.preview",
      grant.operation === "sandbox.preview.expose" &&
        sandboxPreviewConstraintsEqual(
          { port: request.port, access: request.access },
          grant.constraints,
        ),
    );
  }),
);

export const SandboxCheckpointRequestSchema = Schema.Struct({
  ...SandboxTargetRequestFields,
  executionGrant: SandboxExecutionGrantSchema,
  name: TrimmedTextSchema.check(Schema.isMaxLength(256)),
  ttlSeconds: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(300),
    Schema.isLessThanOrEqualTo(30 * 24 * 60 * 60),
  ),
  useGitignore: Schema.Boolean,
  idempotencyKey: IdempotencyKeySchema,
}).check(
  Schema.makeFilter((request) => {
    const grant = request.executionGrant;
    return exactExecutionGrantIssue(
      request,
      "backup",
      "sandbox.checkpoint",
      grant.operation === "sandbox.checkpoint.create" &&
        sandboxCheckpointConstraintsEqual(
          {
            name: request.name,
            ttlSeconds: request.ttlSeconds,
            useGitignore: request.useGitignore,
          },
          grant.constraints,
        ),
    );
  }),
);

export const SandboxRestoreRequestSchema = Schema.Struct({
  ...SandboxTargetRequestFields,
  executionGrant: SandboxExecutionGrantSchema,
  backupId: SandboxBackupIdSchema,
  idempotencyKey: IdempotencyKeySchema,
}).check(
  Schema.makeFilter((request) => {
    const grant = request.executionGrant;
    return exactExecutionGrantIssue(
      request,
      "backup",
      "sandbox.restore",
      grant.operation === "sandbox.checkpoint.restore" &&
        sandboxRestoreConstraintsEqual({ backupId: request.backupId }, grant.constraints),
    );
  }),
);

export const SandboxDestroyRequestSchema = Schema.Struct({
  ...SandboxTargetRequestFields,
  executionGrant: SandboxExecutionGrantSchema,
  reason: TrimmedTextSchema.check(Schema.isMaxLength(500)),
  idempotencyKey: IdempotencyKeySchema,
}).check(
  Schema.makeFilter((request) => {
    const grant = request.executionGrant;
    return exactExecutionGrantIssue(
      request,
      "process",
      "sandbox.destroy",
      grant.operation === "sandbox.destroy" &&
        sandboxDestroyConstraintsEqual({ reason: request.reason }, grant.constraints),
    );
  }),
);

type SandboxStageAOperationRequest =
  | typeof EnsureSandboxRequestSchema.Type
  | typeof SandboxCommandRequestSchema.Type
  | typeof SandboxCheckpointRequestSchema.Type
  | typeof SandboxRestoreRequestSchema.Type
  | typeof SandboxDestroyRequestSchema.Type;

export type SandboxSha256 = (input: Uint8Array) => Promise<ArrayBuffer>;

export class SandboxOperationInputError extends Schema.TaggedError<SandboxOperationInputError>()(
  "SandboxOperationInputError",
  {
    message: Schema.String,
    cause: Schema.optionalKey(Schema.Defect()),
  },
) {}

export interface PreparedSandboxOperationInput {
  readonly operation:
    | "sandbox.ensure"
    | "sandbox.process.execute"
    | "sandbox.checkpoint.create"
    | "sandbox.checkpoint.restore"
    | "sandbox.destroy";
  readonly encodedInput: typeof SandboxPreparedOperationInputSchema.Type;
  readonly inputDigest: typeof SandboxOperationInputDigestSchema.Type;
}

export interface PreparedSandboxWorkspaceFileOperationInput {
  readonly operation: "sandbox.files.list" | "sandbox.files.read";
  readonly encodedInput: typeof SandboxPreparedOperationInputSchema.Type;
  readonly inputDigest: typeof SandboxOperationInputDigestSchema.Type;
}

function canonicalSandboxOperationValue(value: unknown): unknown {
  if (value === null || typeof value === "string" || typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (Array.isArray(value)) return value.map(canonicalSandboxOperationValue);
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter((entry) => entry[1] !== undefined)
        .toSorted(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
        .map(([key, child]) => [key, canonicalSandboxOperationValue(child)]),
    );
  }
  throw new SandboxOperationInputError({
    message: "Sandbox operation input is not canonical.",
  });
}

function sandboxExistingOperationInput(
  request:
    | typeof SandboxCommandRequestSchema.Type
    | typeof SandboxCheckpointRequestSchema.Type
    | typeof SandboxRestoreRequestSchema.Type
    | typeof SandboxDestroyRequestSchema.Type,
  operation: PreparedSandboxOperationInput["operation"],
) {
  return {
    schemaVersion: 1,
    operation,
    target: {
      sandboxId: request.sandboxId,
      expectedGeneration: request.expectedGeneration,
    },
    input: request.executionGrant.constraints,
  } as const;
}

function sandboxOperationInput(request: SandboxStageAOperationRequest): {
  readonly operation: PreparedSandboxOperationInput["operation"];
  readonly value: unknown;
} {
  if ("purpose" in request) {
    return {
      operation: "sandbox.ensure",
      value: {
        schemaVersion: 1,
        operation: "sandbox.ensure",
        intent: {
          workspaceId: request.workspaceId,
          projectId: request.projectId,
          environmentId: request.environmentId,
          ownerUserId: request.ownerUserId,
          runId: request.runId,
          purpose: request.purpose,
          scope: request.scope,
          resourceProfile: request.resourceProfile,
          seed: request.seed,
          requestedCapabilities: request.requestedCapabilities,
          networkPolicyId: request.executionGrant.networkPolicyId,
        },
      },
    };
  }
  if ("argv" in request) {
    return {
      operation: "sandbox.process.execute",
      value: sandboxExistingOperationInput(request, "sandbox.process.execute"),
    };
  }
  if ("name" in request) {
    return {
      operation: "sandbox.checkpoint.create",
      value: sandboxExistingOperationInput(request, "sandbox.checkpoint.create"),
    };
  }
  if ("backupId" in request) {
    return {
      operation: "sandbox.checkpoint.restore",
      value: sandboxExistingOperationInput(request, "sandbox.checkpoint.restore"),
    };
  }
  return {
    operation: "sandbox.destroy",
    value: sandboxExistingOperationInput(request, "sandbox.destroy"),
  };
}

function sandboxWorkspaceFileOperationInput(
  request:
    | typeof SandboxAuthorizedListFilesRequestSchema.Type
    | typeof SandboxAuthorizedReadTextFileRequestSchema.Type,
): {
  readonly operation: PreparedSandboxWorkspaceFileOperationInput["operation"];
  readonly value: unknown;
} {
  const operation = "limit" in request ? "sandbox.files.list" : "sandbox.files.read";
  return {
    operation,
    value: {
      schemaVersion: 1,
      operation,
      target: {
        sandboxId: request.sandboxId,
        expectedGeneration: request.expectedGeneration,
      },
      input:
        operation === "sandbox.files.list"
          ? {
              path: request.path,
              ...("cursor" in request && request.cursor !== undefined
                ? { cursor: request.cursor }
                : {}),
              limit: "limit" in request ? request.limit : SANDBOX_WORKSPACE_FILE_LIST_DEFAULT_LIMIT,
            }
          : {
              path: request.path,
              maxBytes:
                "maxBytes" in request ? request.maxBytes : SANDBOX_TEXT_FILE_READ_DEFAULT_MAX_BYTES,
            },
    },
  };
}

function sandboxDigestHexadecimal(bytes: ArrayBuffer): string {
  return [...new Uint8Array(bytes)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function sandboxCanonicalUtf8(value: string): Uint8Array {
  const bytes: Array<number> = [];
  for (let index = 0; index < value.length; index += 1) {
    const first = value.charCodeAt(index);
    let codePoint = first;
    if (first >= 0xd800 && first <= 0xdbff) {
      const second = value.charCodeAt(index + 1);
      if (second >= 0xdc00 && second <= 0xdfff) {
        codePoint = 0x1_0000 + ((first - 0xd800) << 10) + (second - 0xdc00);
        index += 1;
      } else {
        codePoint = 0xfffd;
      }
    } else if (first >= 0xdc00 && first <= 0xdfff) {
      codePoint = 0xfffd;
    }
    if (codePoint <= 0x7f) {
      bytes.push(codePoint);
    } else if (codePoint <= 0x7ff) {
      bytes.push(0xc0 | (codePoint >> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint <= 0xffff) {
      bytes.push(
        0xe0 | (codePoint >> 12),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >> 18),
        0x80 | ((codePoint >> 12) & 0x3f),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    }
  }
  return Uint8Array.from(bytes);
}

/**
 * Canonical Stage A input protocol shared by grant issuers and execution bridges. The caller
 * supplies SHA-256 so deterministic contract logic does not depend on an ambient crypto runtime.
 */
export const prepareSandboxStageAOperationInput = Effect.fn(
  "SandboxContract.prepareStageAOperationInput",
)(function* (request: SandboxStageAOperationRequest, sha256: SandboxSha256) {
  const projected = sandboxOperationInput(request);
  const encoded = yield* Effect.try({
    try: () => JSON.stringify(canonicalSandboxOperationValue(projected.value)),
    catch: (cause) =>
      new SandboxOperationInputError({
        message: "Sandbox operation input could not be canonically encoded.",
        cause,
      }),
  });
  const encodedInput = yield* Schema.decodeEffect(SandboxPreparedOperationInputSchema)(
    encoded,
  ).pipe(
    Effect.mapError(
      (cause) =>
        new SandboxOperationInputError({
          message: "Sandbox operation input did not satisfy the durable schema.",
          cause,
        }),
    ),
  );
  const digest = yield* Effect.tryPromise({
    try: () => sha256(sandboxCanonicalUtf8(encodedInput)),
    catch: (cause) =>
      new SandboxOperationInputError({
        message: "Sandbox operation input digest could not be computed.",
        cause,
      }),
  });
  const inputDigest = yield* Schema.decodeEffect(SandboxOperationInputDigestSchema)(
    sandboxDigestHexadecimal(digest),
  ).pipe(
    Effect.mapError(
      (cause) =>
        new SandboxOperationInputError({
          message: "Sandbox operation input digest is invalid.",
          cause,
        }),
    ),
  );
  return {
    operation: projected.operation,
    encodedInput,
    inputDigest,
  } satisfies PreparedSandboxOperationInput;
});

/** Canonical digest input for read-only workspace operations; grants and file contents are absent. */
export const prepareSandboxWorkspaceFileOperationInput = Effect.fn(
  "SandboxContract.prepareWorkspaceFileOperationInput",
)(function* (
  request:
    | typeof SandboxAuthorizedListFilesRequestSchema.Type
    | typeof SandboxAuthorizedReadTextFileRequestSchema.Type,
  sha256: SandboxSha256,
) {
  const projected = sandboxWorkspaceFileOperationInput(request);
  const encoded = yield* Effect.try({
    try: () => JSON.stringify(canonicalSandboxOperationValue(projected.value)),
    catch: (cause) =>
      new SandboxOperationInputError({
        message: "Sandbox workspace file input could not be canonically encoded.",
        cause,
      }),
  });
  const encodedInput = yield* Schema.decodeEffect(SandboxPreparedOperationInputSchema)(
    encoded,
  ).pipe(
    Effect.mapError(
      (cause) =>
        new SandboxOperationInputError({
          message: "Sandbox workspace file input did not satisfy the durable schema.",
          cause,
        }),
    ),
  );
  const digest = yield* Effect.tryPromise({
    try: () => sha256(sandboxCanonicalUtf8(encodedInput)),
    catch: (cause) =>
      new SandboxOperationInputError({
        message: "Sandbox workspace file input digest could not be computed.",
        cause,
      }),
  });
  const inputDigest = yield* Schema.decodeEffect(SandboxOperationInputDigestSchema)(
    sandboxDigestHexadecimal(digest),
  ).pipe(
    Effect.mapError(
      (cause) =>
        new SandboxOperationInputError({
          message: "Sandbox workspace file input digest is invalid.",
          cause,
        }),
    ),
  );
  return {
    operation: projected.operation,
    encodedInput,
    inputDigest,
  } satisfies PreparedSandboxWorkspaceFileOperationInput;
});

export const SandboxProcessSchema = Schema.Struct({
  id: SandboxProcessIdSchema,
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
  status: Schema.Literals(["starting", "running", "exited", "killed", "stale", "error"]),
  argv: Schema.Array(ArgvValueSchema).check(Schema.isMinLength(1), Schema.isMaxLength(128)),
  cwd: SandboxPathSchema,
  startedAt: IsoDateTimeSchema,
  finishedAt: Schema.optionalKey(IsoDateTimeSchema),
  exitCode: Schema.optionalKey(Schema.NullOr(Schema.Int)),
  signal: Schema.optionalKey(
    Schema.NullOr(Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(64))),
  ),
});

const BoundedCommandOutputSchema = Schema.String.check(
  Schema.isMaxLength(SANDBOX_COMMAND_OUTPUT_MAX_LENGTH),
);
export const SandboxCommandResultSchema = Schema.Struct({
  process: SandboxProcessSchema,
  stdout: BoundedCommandOutputSchema,
  stderr: BoundedCommandOutputSchema,
  stdoutTruncated: Schema.Boolean,
  stderrTruncated: Schema.Boolean,
  outputSanitized: Schema.Literal(true),
});

export const SandboxRestoreResultSchema = Schema.Struct({
  accepted: Schema.Literal(true),
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
  status: Schema.Literal("ready"),
  restoredBackupId: SandboxBackupIdSchema,
});

export const SandboxDestroyResultSchema = Schema.Struct({
  accepted: Schema.Literal(true),
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
  status: Schema.Literal("destroyed"),
});

export const SandboxFileEntrySchema = Schema.Struct({
  path: SandboxWorkspacePathSchema,
  name: TrimmedTextSchema.check(Schema.isMaxLength(512)),
  kind: Schema.Literals(["file", "directory", "symlink"]),
  sizeBytes: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  modifiedAt: Schema.optionalKey(IsoDateTimeSchema),
  revision: Schema.optionalKey(TrimmedTextSchema.check(Schema.isMaxLength(256))),
});

export const SandboxListFilesResultSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
  path: SandboxWorkspacePathSchema,
  entries: Schema.Array(SandboxFileEntrySchema).check(
    Schema.isMaxLength(SANDBOX_WORKSPACE_FILE_LIST_MAX_LIMIT),
  ),
  nextCursor: Schema.optionalKey(SandboxWorkspacePathSchema),
}).check(
  Schema.makeFilter((result) => {
    if (result.nextCursor === undefined) return true;
    const last = result.entries.at(-1);
    return (
      last?.path === result.nextCursor ||
      "next cursor must identify the final entry in a non-empty page"
    );
  }),
);

export const SandboxReadTextFileResultSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
  path: SandboxWorkspacePathSchema,
  content: Schema.String.check(Schema.isMaxLength(SANDBOX_TEXT_FILE_READ_MAX_BYTES)),
  encoding: Schema.Literal("utf-8"),
  sizeBytes: NonNegativeSafeIntegerSchema.check(
    Schema.isLessThanOrEqualTo(SANDBOX_TEXT_FILE_READ_MAX_BYTES),
  ),
  mimeType: Schema.optionalKey(TrimmedTextSchema.check(Schema.isMaxLength(255))),
});

export const SandboxWorkspaceFileErrorCodeSchema = Schema.Literals([
  "UNAUTHENTICATED",
  "FORBIDDEN",
  "NOT_FOUND",
  "INVALID_INPUT",
  "UNAVAILABLE",
  "CONFLICT",
  "RATE_LIMITED",
  "QUOTA_EXCEEDED",
  "TIMEOUT",
  "CANCELLED",
  "STALE_RESOURCE",
  "INTEGRITY_ERROR",
  "BINARY_FILE",
  "FILE_TOO_LARGE",
  "DIRECTORY_TOO_LARGE",
  "UNSUPPORTED_FILE_TYPE",
]);

export const SandboxWorkspaceFileErrorSchema = Schema.Struct({
  code: SandboxWorkspaceFileErrorCodeSchema,
  message: TrimmedTextSchema.check(Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});

/** A terminal ticket is an ephemeral bearer capability and must never be persisted. */
export const SandboxTerminalTicketSchema = Schema.Struct({
  sandboxId: SandboxIdSchema,
  terminalId: SandboxTerminalIdSchema,
  generation: SandboxGenerationSchema,
  websocketUrl: WebSocketUrlSchema,
  ticket: Schema.String.check(Schema.isMinLength(32), Schema.isMaxLength(8_192)),
  expiresAt: IsoDateTimeSchema,
});

/** Preview URLs are ephemeral capabilities; durable events use SandboxPreviewEventReferenceSchema. */
export const SandboxPreviewSchema = Schema.Struct({
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
  port: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1_024), Schema.isLessThanOrEqualTo(65_535)),
  url: HttpUrlSchema,
  access: SandboxPreviewAccessSchema,
  expiresAt: Schema.optionalKey(IsoDateTimeSchema),
});

export const SandboxBackupSchema = Schema.Struct({
  id: SandboxBackupIdSchema,
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  directory: SandboxWorkspacePathSchema,
  name: TrimmedTextSchema.check(Schema.isMaxLength(256)),
  status: Schema.Literals(["creating", "ready", "restoring", "expired", "deleted", "error"]),
  createdAt: IsoDateTimeSchema,
  expiresAt: IsoDateTimeSchema,
  sizeBytes: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  sdkVersion: Schema.optionalKey(TrimmedTextSchema.check(Schema.isMaxLength(120))),
  imageDigest: Schema.optionalKey(TrimmedTextSchema.check(Schema.isMaxLength(256))),
});

export const ArtifactObjectKindSchema = Schema.Literals([
  "source-file",
  "artifact-revision",
  "export",
  "thumbnail",
  "attachment",
  "generated-file",
  "screenshot",
  "dataset",
  "sandbox-output",
  "diagnostic",
]);
export const ArtifactObjectStatusSchema = Schema.Literals([
  "pending-upload",
  "uploaded",
  "processing",
  "ready",
  "quarantined",
  "deleted",
  "error",
]);
export const ArtifactRetentionSchema = Schema.Literals([
  "temporary",
  "workspace",
  "pinned",
  "compliance",
]);
const LowerHexSha256Schema = SandboxOperationInputDigestSchema;

/** Convex owns this metadata; storageKey is internal and never enters durable activity events. */
export const ArtifactObjectSchema = Schema.Struct({
  id: ArtifactObjectIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  artifactId: Schema.optionalKey(ArtifactIdSchema),
  revisionId: Schema.optionalKey(ArtifactRevisionIdSchema),
  sandboxId: Schema.optionalKey(SandboxIdSchema),
  runId: Schema.optionalKey(CanonicalIdSchema),
  kind: ArtifactObjectKindSchema,
  status: ArtifactObjectStatusSchema,
  fileName: TrimmedTextSchema.check(Schema.isMaxLength(512)),
  contentType: TrimmedTextSchema.check(Schema.isMaxLength(255)),
  sizeBytes: NonNegativeSafeIntegerSchema,
  checksumSha256: Schema.optionalKey(LowerHexSha256Schema),
  storageProvider: Schema.Literal("cloudflare-r2"),
  storageKey: R2StorageKeySchema,
  retention: ArtifactRetentionSchema,
  createdBy: Schema.optionalKey(UserIdSchema),
  createdAt: IsoDateTimeSchema,
  expiresAt: Schema.optionalKey(IsoDateTimeSchema),
});

const uploadHeaderNameIssue = (value: string): true | string =>
  (value.length >= 1 &&
    value.length <= 128 &&
    /^[a-z0-9-]+$/.test(value) &&
    !["authorization", "cookie", "proxy-authorization", "set-cookie"].includes(value)) ||
  "must be an admitted non-secret upload header";
const UploadHeaderValueSchema = Schema.String.check(
  Schema.isMaxLength(4_096),
  Schema.makeFilter(
    (value) =>
      (!value.includes("\0") && !value.includes("\r") && !value.includes("\n")) ||
      "must be a single-line header value",
  ),
);
const UploadRequiredHeadersSchema = Schema.Record(Schema.String, UploadHeaderValueSchema).check(
  Schema.makeFilter(
    (headers) =>
      Object.keys(headers).every((name) => uploadHeaderNameIssue(name) === true) ||
      "must contain only admitted non-secret upload headers",
  ),
  Schema.makeFilter(
    (headers) => Object.keys(headers).length <= 32 || "must contain at most 32 headers",
  ),
);

/** Upload intents contain ephemeral capabilities and must never be persisted or emitted durably. */
export const UploadIntentSchema = Schema.Struct({
  uploadId: UploadIdSchema,
  objectId: ArtifactObjectIdSchema,
  method: Schema.Literals(["PUT", "MULTIPART"]),
  uploadUrl: Schema.optionalKey(HttpUrlSchema),
  requiredHeaders: UploadRequiredHeadersSchema,
  multipartUploadId: Schema.optionalKey(TrimmedTextSchema.check(Schema.isMaxLength(512))),
  partSizeBytes: Schema.optionalKey(PositiveSafeIntegerSchema),
  maxParts: Schema.optionalKey(PositiveSafeIntegerSchema.check(Schema.isLessThanOrEqualTo(10_000))),
  expiresAt: IsoDateTimeSchema,
  maxBytes: PositiveSafeIntegerSchema,
});

export const MultipartPartTicketSchema = Schema.Struct({
  uploadId: UploadIdSchema,
  partNumber: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(10_000),
  ),
  uploadUrl: HttpUrlSchema,
  requiredHeaders: UploadRequiredHeadersSchema,
  expiresAt: IsoDateTimeSchema,
});

export const MultipartCompletedPartSchema = Schema.Struct({
  partNumber: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(1),
    Schema.isLessThanOrEqualTo(10_000),
  ),
  etag: TrimmedTextSchema.check(Schema.isMaxLength(512)),
  checksumSha256: Schema.optionalKey(
    Schema.String.check(
      Schema.makeFilter(
        (value) => /^[A-Za-z0-9+/=:-]{16,256}$/.test(value) || "must be a valid checksum token",
      ),
    ),
  ),
});

export const CreateUploadIntentRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  idempotencyKey: IdempotencyKeySchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  artifactId: Schema.optionalKey(ArtifactIdSchema),
  revisionId: Schema.optionalKey(ArtifactRevisionIdSchema),
  kind: ArtifactObjectKindSchema,
  fileName: TrimmedTextSchema.check(Schema.isMaxLength(512)),
  contentType: TrimmedTextSchema.check(Schema.isMaxLength(255)),
  sizeBytes: NonNegativeSafeIntegerSchema,
  retention: ArtifactRetentionSchema,
});

export const MultipartPartTicketsRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  objectId: ArtifactObjectIdSchema,
  uploadId: UploadIdSchema,
  partNumbers: Schema.Array(
    Schema.Int.check(Schema.isGreaterThanOrEqualTo(1), Schema.isLessThanOrEqualTo(10_000)),
  ).check(Schema.isMinLength(1), Schema.isMaxLength(100)),
});

export const CompleteMultipartUploadRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  idempotencyKey: IdempotencyKeySchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  objectId: ArtifactObjectIdSchema,
  uploadId: UploadIdSchema,
  parts: Schema.Array(MultipartCompletedPartSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(10_000),
  ),
});

export const AbortUploadRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  idempotencyKey: IdempotencyKeySchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  objectId: ArtifactObjectIdSchema,
  uploadId: UploadIdSchema,
});

export const FinalizeUploadRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  idempotencyKey: IdempotencyKeySchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  objectId: ArtifactObjectIdSchema,
  uploadEvidence: Schema.Struct({
    etag: Schema.optionalKey(TrimmedTextSchema.check(Schema.isMaxLength(512))),
    checksumSha256: Schema.optionalKey(LowerHexSha256Schema),
  }),
});

export const ArtifactDownloadRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  objectId: ArtifactObjectIdSchema,
  expiresInSeconds: Schema.Int.check(
    Schema.isGreaterThanOrEqualTo(30),
    Schema.isLessThanOrEqualTo(3_600),
  ),
});

export const DeleteArtifactObjectRequestSchema = Schema.Struct({
  requestId: RequestIdSchema,
  idempotencyKey: IdempotencyKeySchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  objectId: ArtifactObjectIdSchema,
  reason: TrimmedTextSchema.check(Schema.isMaxLength(500)),
});

const SandboxEventBaseFields = {
  id: CanonicalIdSchema,
  sequence: NonNegativeSafeIntegerSchema,
  requestId: RequestIdSchema,
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  occurredAt: IsoDateTimeSchema,
} as const;
const DurableEventMarkerSchema = Schema.Literal("durable");
const TransientEventMarkerSchema = Schema.Literal("transient");

const SandboxPreviewEventReferenceSchema = Schema.Struct({
  sandboxId: SandboxIdSchema,
  generation: SandboxGenerationSchema,
  port: Schema.Int.check(Schema.isGreaterThanOrEqualTo(1_024), Schema.isLessThanOrEqualTo(65_535)),
  access: SandboxPreviewAccessSchema,
  expiresAt: Schema.optionalKey(IsoDateTimeSchema),
});
const ArtifactObjectEventReferenceSchema = Schema.Struct({
  id: ArtifactObjectIdSchema,
  artifactId: Schema.optionalKey(ArtifactIdSchema),
  revisionId: Schema.optionalKey(ArtifactRevisionIdSchema),
  kind: ArtifactObjectKindSchema,
  status: ArtifactObjectStatusSchema,
  fileName: TrimmedTextSchema.check(Schema.isMaxLength(512)),
  contentType: TrimmedTextSchema.check(Schema.isMaxLength(255)),
  sizeBytes: NonNegativeSafeIntegerSchema,
  checksumSha256: Schema.optionalKey(LowerHexSha256Schema),
  retention: ArtifactRetentionSchema,
});
export const SandboxRuntimeErrorCodeSchema = Schema.Literals([
  "SANDBOX_CONTAINER_UNAVAILABLE",
  "SANDBOX_OPERATION_INTERRUPTED",
  "SANDBOX_RPC_TRANSPORT",
  "SANDBOX_PROCESS_STALE",
  "SANDBOX_TERMINAL_STALE",
  "SANDBOX_BACKUP_EXPIRED",
  "SANDBOX_BACKUP_INCOMPATIBLE",
  "SANDBOX_QUOTA_EXCEEDED",
  "R2_UPLOAD_EXPIRED",
  "R2_OBJECT_MISMATCH",
  "SANDBOX_POLICY_DENIED",
  "SANDBOX_INVALID_INPUT",
]);

export const SandboxDurableRuntimeEventSchema = Schema.Union([
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: DurableEventMarkerSchema,
    type: Schema.Literal("sandbox.status"),
    status: SandboxStatusSchema,
    summary: TrimmedTextSchema.check(Schema.isMaxLength(500)),
  }),
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: DurableEventMarkerSchema,
    type: Schema.Literal("sandbox.process.started"),
    processId: SandboxProcessIdSchema,
    argvSummary: TrimmedTextSchema.check(Schema.isMaxLength(500)),
  }),
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: DurableEventMarkerSchema,
    type: Schema.Literal("sandbox.process.completed"),
    processId: SandboxProcessIdSchema,
    exitCode: Schema.NullOr(Schema.Int),
    durationMs: NonNegativeSafeIntegerSchema,
  }),
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: DurableEventMarkerSchema,
    type: Schema.Literal("sandbox.preview.ready"),
    preview: SandboxPreviewEventReferenceSchema,
  }),
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: DurableEventMarkerSchema,
    type: Schema.Literal("sandbox.backup.ready"),
    backup: SandboxBackupSchema,
  }),
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: DurableEventMarkerSchema,
    type: Schema.Literal("sandbox.artifact.ready"),
    object: ArtifactObjectEventReferenceSchema,
  }),
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: DurableEventMarkerSchema,
    type: Schema.Literal("sandbox.cost.updated"),
    estimatedProviderCostMicros: NonNegativeSafeIntegerSchema,
    currency: Schema.String.check(
      Schema.isLengthBetween(3, 3),
      Schema.makeFilter((value) => /^[A-Z]{3}$/.test(value) || "must be an uppercase currency"),
    ),
  }),
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: DurableEventMarkerSchema,
    type: Schema.Literal("sandbox.error"),
    code: SandboxRuntimeErrorCodeSchema,
    summary: TrimmedTextSchema.check(Schema.isMaxLength(1_000)),
    retryable: Schema.Boolean,
  }),
]);

export const SandboxTransientRuntimeEventSchema = Schema.Union([
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: TransientEventMarkerSchema,
    type: Schema.Literal("sandbox.process.output"),
    processId: SandboxProcessIdSchema,
    stream: Schema.Literals(["stdout", "stderr"]),
    chunk: Schema.String.check(Schema.isMaxLength(65_536)),
    transient: Schema.Literal(true),
  }),
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: TransientEventMarkerSchema,
    type: Schema.Literal("sandbox.terminal.ready"),
    terminalId: SandboxTerminalIdSchema,
  }),
  Schema.Struct({
    ...SandboxEventBaseFields,
    durability: TransientEventMarkerSchema,
    type: Schema.Literal("sandbox.file.changed"),
    path: SandboxPathSchema,
    operation: Schema.Literals(["created", "modified", "deleted", "renamed"]),
  }),
]);
export const SandboxRuntimeEventSchema = Schema.Union([
  SandboxDurableRuntimeEventSchema,
  SandboxTransientRuntimeEventSchema,
]);

export const SandboxStageAOperationSchema = Schema.Literals([
  "sandbox.ensure",
  "sandbox.process.execute",
  "sandbox.checkpoint.create",
  "sandbox.checkpoint.restore",
  "sandbox.destroy",
]);
export const SandboxExistingStageAOperationSchema = Schema.Literals([
  "sandbox.process.execute",
  "sandbox.checkpoint.create",
  "sandbox.checkpoint.restore",
  "sandbox.destroy",
]);

const SandboxMetadataAuthorityFields = {
  workspaceId: WorkspaceIdSchema,
  projectId: ProjectIdSchema,
  runId: CanonicalIdSchema,
  attemptId: CanonicalIdSchema,
  attemptGeneration: PositiveSafeIntegerSchema,
  actorUserId: UserIdSchema,
  operationId: CanonicalIdSchema,
  toolCallId: CanonicalIdSchema,
  inputDigest: SandboxOperationInputDigestSchema,
  executionClaimGeneration: PositiveSafeIntegerSchema,
  requestId: RequestIdSchema,
  idempotencyKey: IdempotencyKeySchema,
} as const;

const durableOperationInputIssue = (value: string): true | string => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value) as unknown;
  } catch {
    return "must be valid JSON";
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return "must encode one operation input object";
  }
  const forbiddenKey =
    /^(?:authorization|cookie|executionGrant|password|rawBytes|secret|signedUrl|stderr|stdout|storageKey|ticket|token)$/i;
  const pending: Array<unknown> = [parsed];
  while (pending.length > 0) {
    const candidate = pending.pop();
    if (candidate === null || typeof candidate !== "object") continue;
    if (Array.isArray(candidate)) {
      pending.push(...candidate);
      continue;
    }
    for (const [key, child] of Object.entries(candidate)) {
      if (forbiddenKey.test(key)) {
        return "must not contain grants, secrets, capability URLs, storage keys, or raw output";
      }
      pending.push(child);
    }
  }
  return true;
};

/** Exact safe input persisted with the canonical agent operation; grants and raw output are absent. */
export const SandboxPreparedOperationInputSchema = Schema.String.check(
  Schema.isMinLength(2),
  Schema.isMaxLength(65_536),
  Schema.makeFilter(durableOperationInputIssue),
).pipe(Schema.brand("SandboxPreparedOperationInput"));

export const SandboxPrepareOperationRequestSchema = Schema.Struct({
  ...SandboxMetadataAuthorityFields,
  operation: SandboxStageAOperationSchema,
  encodedInput: SandboxPreparedOperationInputSchema,
});
export const SandboxPrepareOperationResultSchema = Schema.Struct({
  status: Schema.Literals(["queued", "running", "succeeded", "failed", "uncertain"]),
  replay: Schema.Boolean,
});

export const SandboxBeginEnsureRequestSchema = Schema.Struct({
  ...SandboxMetadataAuthorityFields,
  environmentId: Schema.optionalKey(EnvironmentIdSchema),
  ownerUserId: Schema.optionalKey(UserIdSchema),
  purpose: SandboxPurposeSchema,
  scope: SandboxScopeSchema,
  resourceProfile: Schema.Literal("standard"),
  seed: Schema.Union([
    Schema.Struct({ type: Schema.Literal("empty") }),
    Schema.Struct({ type: Schema.Literal("backup"), backupId: SandboxBackupIdSchema }),
  ]),
  requestedCapabilities: Schema.Array(SandboxCapabilitySchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(SANDBOX_STAGE_A_CAPABILITIES.length),
  ),
  networkPolicyId: Schema.Literal(SANDBOX_STAGE_A_NETWORK_POLICY_ID),
  idlePolicy: Schema.Literal("destroy"),
}).check(
  Schema.makeFilter(
    (request) =>
      request.scope !== "user-project" ||
      request.ownerUserId === request.actorUserId ||
      "user-project sandbox scope requires the exact actor owner",
  ),
);
export const SandboxBeginExistingOperationRequestSchema = Schema.Struct({
  ...SandboxMetadataAuthorityFields,
  sandboxId: SandboxIdSchema,
  operation: SandboxExistingStageAOperationSchema,
  expectedGeneration: SandboxGenerationSchema,
  backupId: Schema.optionalKey(SandboxBackupIdSchema),
});
export const SandboxBeginOperationResultSchema = Schema.Struct({
  sandboxId: SandboxIdSchema,
  sandboxVersion: PositiveSafeIntegerSchema,
  status: Schema.Literals(["accepted", "succeeded", "failed", "uncertain"]),
  replay: Schema.Boolean,
}).check(
  Schema.makeFilter(
    (result) =>
      result.replay ||
      result.status === "accepted" ||
      "a newly accepted operation must have accepted status",
  ),
);

export const SandboxMetadataBackupSchema = Schema.Struct({
  backupId: SandboxBackupIdSchema,
  sourceGeneration: SandboxGenerationSchema,
  directory: Schema.Literal("/workspace"),
  name: TrimmedTextSchema.check(Schema.isMaxLength(256)),
  createdAt: NonNegativeSafeIntegerSchema,
  expiresAt: NonNegativeSafeIntegerSchema,
  sizeBytes: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  sdkVersion: Schema.optionalKey(TrimmedTextSchema.check(Schema.isMaxLength(120))),
  imageDigest: Schema.optionalKey(TrimmedTextSchema.check(Schema.isMaxLength(256))),
  schemaVersion: Schema.Literal(1),
  retentionClass: Schema.Literals(["temporary", "workspace"]),
});
export const SandboxCompleteOperationRequestSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  operationId: CanonicalIdSchema,
  requestId: RequestIdSchema,
  executionClaimGeneration: PositiveSafeIntegerSchema,
  outcome: Schema.Literals(["succeeded", "failed", "uncertain"]),
  observedGeneration: Schema.optionalKey(SandboxGenerationSchema),
  persistence: Schema.optionalKey(SandboxPersistenceSchema),
  commandCount: NonNegativeSafeIntegerSchema,
  errorCode: Schema.optionalKey(
    Schema.String.check(
      Schema.isMinLength(1),
      Schema.isMaxLength(120),
      Schema.makeFilter(
        (value) => /^[A-Z0-9][A-Z0-9_.-]*$/.test(value) || "must be a safe error code",
      ),
    ),
  ),
  reconcileAfter: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  backup: Schema.optionalKey(SandboxMetadataBackupSchema),
});
export const SandboxCompleteOperationResultSchema = Schema.Struct({
  sandboxVersion: PositiveSafeIntegerSchema,
  replay: Schema.Boolean,
});
export const SandboxMetadataErrorSchema = Schema.Struct({
  code: Schema.String.check(
    Schema.isMinLength(1),
    Schema.isMaxLength(120),
    Schema.makeFilter(
      (value) => /^[A-Z][A-Z0-9_]*$/.test(value) || "must be an uppercase machine code",
    ),
  ),
  message: TrimmedTextSchema.check(Schema.isMaxLength(240)),
});

export const decodeSandboxPrepareOperationRequest = Schema.decodeUnknownSync(
  SandboxPrepareOperationRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxPrepareOperationRequest = Schema.encodeSync(
  SandboxPrepareOperationRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxPrepareOperationResult = Schema.decodeUnknownSync(
  SandboxPrepareOperationResultSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxPrepareOperationResult = Schema.encodeSync(
  SandboxPrepareOperationResultSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxBeginEnsureRequest = Schema.decodeUnknownSync(
  SandboxBeginEnsureRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxBeginEnsureRequest = Schema.encodeSync(
  SandboxBeginEnsureRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxBeginExistingOperationRequest = Schema.decodeUnknownSync(
  SandboxBeginExistingOperationRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxBeginExistingOperationRequest = Schema.encodeSync(
  SandboxBeginExistingOperationRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxBeginOperationResult = Schema.decodeUnknownSync(
  SandboxBeginOperationResultSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxBeginOperationResult = Schema.encodeSync(
  SandboxBeginOperationResultSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxCompleteOperationRequest = Schema.decodeUnknownSync(
  SandboxCompleteOperationRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxCompleteOperationRequest = Schema.encodeSync(
  SandboxCompleteOperationRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxCompleteOperationResult = Schema.decodeUnknownSync(
  SandboxCompleteOperationResultSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxCompleteOperationResult = Schema.encodeSync(
  SandboxCompleteOperationResultSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxMetadataError = Schema.decodeUnknownSync(
  SandboxMetadataErrorSchema,
  SandboxBoundaryParseOptions,
);

export const decodeSandboxExecutionGrant = Schema.decodeUnknownSync(
  SandboxExecutionGrantSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxExecutionGrant = Schema.encodeSync(
  SandboxExecutionGrantSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxEnsureExecutionGrant = Schema.decodeUnknownSync(
  SandboxEnsureExecutionGrantSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxEnsureExecutionGrant = Schema.encodeSync(
  SandboxEnsureExecutionGrantSchema,
  SandboxBoundaryParseOptions,
);
export const decodeEnsureSandboxRequest = Schema.decodeUnknownSync(
  EnsureSandboxRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeEnsureSandboxRequest = Schema.encodeSync(
  EnsureSandboxRequestSchema,
  SandboxBoundaryParseOptions,
);
interface SandboxEnsureRequestBinding {
  readonly sandboxId?: string;
  readonly executionGrant: { readonly sandboxId?: string };
}

export function assertSandboxWorkerEnsureRequestBound<T extends SandboxEnsureRequestBinding>(
  request: T,
): asserts request is T & {
  readonly sandboxId: string;
  readonly executionGrant: T["executionGrant"] & { readonly sandboxId: string };
} {
  if (
    request.sandboxId === undefined ||
    request.executionGrant.sandboxId === undefined ||
    request.sandboxId !== request.executionGrant.sandboxId
  ) {
    throw new Error("Worker ensure request must bind one exact allocated sandboxId");
  }
}
export function decodeSandboxWorkerEnsureRequest(input: unknown): SandboxWorkerEnsureRequest {
  const request = decodeEnsureSandboxRequest(input);
  assertSandboxWorkerEnsureRequestBound(request);
  return request;
}
export function encodeSandboxWorkerEnsureRequest(
  request: SandboxWorkerEnsureRequest,
): SandboxWorkerEnsureRequestEncoded {
  const encoded = encodeEnsureSandboxRequest(request);
  assertSandboxWorkerEnsureRequestBound(encoded);
  return encoded;
}
export const decodeSandboxCommandRequest = Schema.decodeUnknownSync(
  SandboxCommandRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxCommandRequest = Schema.encodeSync(
  SandboxCommandRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxCredentialProxyReference = Schema.decodeUnknownSync(
  SandboxCredentialProxyReferenceSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxCredentialProxyReference = Schema.encodeSync(
  SandboxCredentialProxyReferenceSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxProcessStopRequest = Schema.decodeUnknownSync(
  SandboxProcessStopRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxProcessStopRequest = Schema.encodeSync(
  SandboxProcessStopRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxTerminalRequest = Schema.decodeUnknownSync(
  SandboxTerminalRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxTerminalRequest = Schema.encodeSync(
  SandboxTerminalRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxEventStreamRequest = Schema.decodeUnknownSync(
  SandboxEventStreamRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxEventStreamRequest = Schema.encodeSync(
  SandboxEventStreamRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxListFilesRequest = Schema.decodeUnknownSync(
  SandboxListFilesRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxListFilesRequest = Schema.encodeSync(
  SandboxListFilesRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxReadTextFileRequest = Schema.decodeUnknownSync(
  SandboxReadTextFileRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxReadTextFileRequest = Schema.encodeSync(
  SandboxReadTextFileRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxAuthorizeWorkspaceFileReadRequest = Schema.decodeUnknownSync(
  SandboxAuthorizeWorkspaceFileReadRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxAuthorizeWorkspaceFileReadRequest = Schema.encodeSync(
  SandboxAuthorizeWorkspaceFileReadRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxDiscoverReadableRuntimesRequest = Schema.decodeUnknownSync(
  SandboxDiscoverReadableRuntimesRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxDiscoverReadableRuntimesRequest = Schema.encodeSync(
  SandboxDiscoverReadableRuntimesRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxDiscoverReadableRuntimesResult = Schema.decodeUnknownSync(
  SandboxDiscoverReadableRuntimesResultSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxDiscoverReadableRuntimesResult = Schema.encodeSync(
  SandboxDiscoverReadableRuntimesResultSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxWorkspaceFileAuthority = Schema.decodeUnknownSync(
  SandboxWorkspaceFileAuthoritySchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxWorkspaceFileAuthority = Schema.encodeSync(
  SandboxWorkspaceFileAuthoritySchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxAuthorizedListFilesRequest = Schema.decodeUnknownSync(
  SandboxAuthorizedListFilesRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxAuthorizedReadTextFileRequest = Schema.decodeUnknownSync(
  SandboxAuthorizedReadTextFileRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxWorkerListFilesRequest = Schema.decodeUnknownSync(
  SandboxWorkerListFilesRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxWorkerListFilesRequest = Schema.encodeSync(
  SandboxWorkerListFilesRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxWorkerReadTextFileRequest = Schema.decodeUnknownSync(
  SandboxWorkerReadTextFileRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxWorkerReadTextFileRequest = Schema.encodeSync(
  SandboxWorkerReadTextFileRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxWriteTextFileRequest = Schema.decodeUnknownSync(
  SandboxWriteTextFileRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxWriteTextFileRequest = Schema.encodeSync(
  SandboxWriteTextFileRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxPreviewRequest = Schema.decodeUnknownSync(
  SandboxPreviewRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxPreviewRequest = Schema.encodeSync(
  SandboxPreviewRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxCheckpointRequest = Schema.decodeUnknownSync(
  SandboxCheckpointRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxCheckpointRequest = Schema.encodeSync(
  SandboxCheckpointRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxRestoreRequest = Schema.decodeUnknownSync(
  SandboxRestoreRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxRestoreRequest = Schema.encodeSync(
  SandboxRestoreRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxDestroyRequest = Schema.decodeUnknownSync(
  SandboxDestroyRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxDestroyRequest = Schema.encodeSync(
  SandboxDestroyRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxReference = Schema.decodeUnknownSync(
  SandboxReferenceSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxReference = Schema.encodeSync(
  SandboxReferenceSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxProcess = Schema.decodeUnknownSync(
  SandboxProcessSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxProcess = Schema.encodeSync(
  SandboxProcessSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxCommandResult = Schema.decodeUnknownSync(
  SandboxCommandResultSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxCommandResult = Schema.encodeSync(
  SandboxCommandResultSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxRestoreResult = Schema.decodeUnknownSync(
  SandboxRestoreResultSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxRestoreResult = Schema.encodeSync(
  SandboxRestoreResultSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxDestroyResult = Schema.decodeUnknownSync(
  SandboxDestroyResultSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxDestroyResult = Schema.encodeSync(
  SandboxDestroyResultSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxFileEntry = Schema.decodeUnknownSync(
  SandboxFileEntrySchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxFileEntry = Schema.encodeSync(
  SandboxFileEntrySchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxListFilesResult = Schema.decodeUnknownSync(
  SandboxListFilesResultSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxListFilesResult = Schema.encodeSync(
  SandboxListFilesResultSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxReadTextFileResult = Schema.decodeUnknownSync(
  SandboxReadTextFileResultSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxReadTextFileResult = Schema.encodeSync(
  SandboxReadTextFileResultSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxWorkspaceFileError = Schema.decodeUnknownSync(
  SandboxWorkspaceFileErrorSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxWorkspaceFileError = Schema.encodeSync(
  SandboxWorkspaceFileErrorSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxTerminalTicket = Schema.decodeUnknownSync(
  SandboxTerminalTicketSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxTerminalTicket = Schema.encodeSync(
  SandboxTerminalTicketSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxPreview = Schema.decodeUnknownSync(
  SandboxPreviewSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxPreview = Schema.encodeSync(
  SandboxPreviewSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxBackup = Schema.decodeUnknownSync(
  SandboxBackupSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxBackup = Schema.encodeSync(
  SandboxBackupSchema,
  SandboxBoundaryParseOptions,
);
export const decodeArtifactObject = Schema.decodeUnknownSync(
  ArtifactObjectSchema,
  SandboxBoundaryParseOptions,
);
export const encodeArtifactObject = Schema.encodeSync(
  ArtifactObjectSchema,
  SandboxBoundaryParseOptions,
);
export const decodeUploadIntent = Schema.decodeUnknownSync(
  UploadIntentSchema,
  SandboxBoundaryParseOptions,
);
export const encodeUploadIntent = Schema.encodeSync(
  UploadIntentSchema,
  SandboxBoundaryParseOptions,
);
export const decodeMultipartPartTicket = Schema.decodeUnknownSync(
  MultipartPartTicketSchema,
  SandboxBoundaryParseOptions,
);
export const encodeMultipartPartTicket = Schema.encodeSync(
  MultipartPartTicketSchema,
  SandboxBoundaryParseOptions,
);
export const decodeMultipartCompletedPart = Schema.decodeUnknownSync(
  MultipartCompletedPartSchema,
  SandboxBoundaryParseOptions,
);
export const encodeMultipartCompletedPart = Schema.encodeSync(
  MultipartCompletedPartSchema,
  SandboxBoundaryParseOptions,
);
export const decodeCreateUploadIntentRequest = Schema.decodeUnknownSync(
  CreateUploadIntentRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeCreateUploadIntentRequest = Schema.encodeSync(
  CreateUploadIntentRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeMultipartPartTicketsRequest = Schema.decodeUnknownSync(
  MultipartPartTicketsRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeMultipartPartTicketsRequest = Schema.encodeSync(
  MultipartPartTicketsRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeCompleteMultipartUploadRequest = Schema.decodeUnknownSync(
  CompleteMultipartUploadRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeCompleteMultipartUploadRequest = Schema.encodeSync(
  CompleteMultipartUploadRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeAbortUploadRequest = Schema.decodeUnknownSync(
  AbortUploadRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeAbortUploadRequest = Schema.encodeSync(
  AbortUploadRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeFinalizeUploadRequest = Schema.decodeUnknownSync(
  FinalizeUploadRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeFinalizeUploadRequest = Schema.encodeSync(
  FinalizeUploadRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeArtifactDownloadRequest = Schema.decodeUnknownSync(
  ArtifactDownloadRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeArtifactDownloadRequest = Schema.encodeSync(
  ArtifactDownloadRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeDeleteArtifactObjectRequest = Schema.decodeUnknownSync(
  DeleteArtifactObjectRequestSchema,
  SandboxBoundaryParseOptions,
);
export const encodeDeleteArtifactObjectRequest = Schema.encodeSync(
  DeleteArtifactObjectRequestSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxDurableRuntimeEvent = Schema.decodeUnknownSync(
  SandboxDurableRuntimeEventSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxDurableRuntimeEvent = Schema.encodeSync(
  SandboxDurableRuntimeEventSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxTransientRuntimeEvent = Schema.decodeUnknownSync(
  SandboxTransientRuntimeEventSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxTransientRuntimeEvent = Schema.encodeSync(
  SandboxTransientRuntimeEventSchema,
  SandboxBoundaryParseOptions,
);
export const decodeSandboxRuntimeEvent = Schema.decodeUnknownSync(
  SandboxRuntimeEventSchema,
  SandboxBoundaryParseOptions,
);
export const encodeSandboxRuntimeEvent = Schema.encodeSync(
  SandboxRuntimeEventSchema,
  SandboxBoundaryParseOptions,
);

export type SandboxId = typeof SandboxIdSchema.Type;
export type SandboxIdEncoded = typeof SandboxIdSchema.Encoded;
export type SandboxProcessId = typeof SandboxProcessIdSchema.Type;
export type SandboxProcessIdEncoded = typeof SandboxProcessIdSchema.Encoded;
export type SandboxTerminalId = typeof SandboxTerminalIdSchema.Type;
export type SandboxTerminalIdEncoded = typeof SandboxTerminalIdSchema.Encoded;
export type SandboxBackupId = typeof SandboxBackupIdSchema.Type;
export type SandboxBackupIdEncoded = typeof SandboxBackupIdSchema.Encoded;
export type ArtifactObjectId = typeof ArtifactObjectIdSchema.Type;
export type ArtifactObjectIdEncoded = typeof ArtifactObjectIdSchema.Encoded;
export type UploadId = typeof UploadIdSchema.Type;
export type UploadIdEncoded = typeof UploadIdSchema.Encoded;
export type SandboxGeneration = typeof SandboxGenerationSchema.Type;
export type SandboxGenerationEncoded = typeof SandboxGenerationSchema.Encoded;
export type SandboxPath = typeof SandboxPathSchema.Type;
export type SandboxPathEncoded = typeof SandboxPathSchema.Encoded;
export type SandboxWorkspacePath = typeof SandboxWorkspacePathSchema.Type;
export type SandboxWorkspacePathEncoded = typeof SandboxWorkspacePathSchema.Encoded;
export type SandboxGitRepositoryUrl = typeof SandboxGitRepositoryUrlSchema.Type;
export type SandboxGitRepositoryUrlEncoded = typeof SandboxGitRepositoryUrlSchema.Encoded;
export type ExecutionTarget = typeof ExecutionTargetSchema.Type;
export type ExecutionTargetEncoded = typeof ExecutionTargetSchema.Encoded;
export type SandboxPurpose = typeof SandboxPurposeSchema.Type;
export type SandboxPurposeEncoded = typeof SandboxPurposeSchema.Encoded;
export type SandboxScope = typeof SandboxScopeSchema.Type;
export type SandboxScopeEncoded = typeof SandboxScopeSchema.Encoded;
export type SandboxResourceProfile = typeof SandboxResourceProfileSchema.Type;
export type SandboxResourceProfileEncoded = typeof SandboxResourceProfileSchema.Encoded;
export type SandboxStatus = typeof SandboxStatusSchema.Type;
export type SandboxStatusEncoded = typeof SandboxStatusSchema.Encoded;
export type SandboxPersistence = typeof SandboxPersistenceSchema.Type;
export type SandboxPersistenceEncoded = typeof SandboxPersistenceSchema.Encoded;
export type SandboxSeed = typeof SandboxSeedSchema.Type;
export type SandboxSeedEncoded = typeof SandboxSeedSchema.Encoded;
export type SandboxCapability = typeof SandboxCapabilitySchema.Type;
export type SandboxCapabilityEncoded = typeof SandboxCapabilitySchema.Encoded;
export type SandboxPermission = typeof SandboxPermissionSchema.Type;
export type SandboxPermissionEncoded = typeof SandboxPermissionSchema.Encoded;
export type SandboxOperation = typeof SandboxOperationSchema.Type;
export type SandboxOperationEncoded = typeof SandboxOperationSchema.Encoded;
export type SandboxExecutionGrant = typeof SandboxExecutionGrantSchema.Type;
export type SandboxExecutionGrantEncoded = typeof SandboxExecutionGrantSchema.Encoded;
export type SandboxEnsureExecutionGrant = typeof SandboxEnsureExecutionGrantSchema.Type;
export type SandboxEnsureExecutionGrantEncoded = typeof SandboxEnsureExecutionGrantSchema.Encoded;
export type SandboxOperationInputDigest = typeof SandboxOperationInputDigestSchema.Type;
export type SandboxOperationInputDigestEncoded = typeof SandboxOperationInputDigestSchema.Encoded;
export type SandboxStageAOperation = typeof SandboxStageAOperationSchema.Type;
export type SandboxStageAOperationEncoded = typeof SandboxStageAOperationSchema.Encoded;
export type SandboxExistingStageAOperation = typeof SandboxExistingStageAOperationSchema.Type;
export type SandboxExistingStageAOperationEncoded =
  typeof SandboxExistingStageAOperationSchema.Encoded;
export type SandboxPreparedOperationInput = typeof SandboxPreparedOperationInputSchema.Type;
export type SandboxPreparedOperationInputEncoded =
  typeof SandboxPreparedOperationInputSchema.Encoded;
export type SandboxPrepareOperationRequest = typeof SandboxPrepareOperationRequestSchema.Type;
export type SandboxPrepareOperationRequestEncoded =
  typeof SandboxPrepareOperationRequestSchema.Encoded;
export type SandboxPrepareOperationResult = typeof SandboxPrepareOperationResultSchema.Type;
export type SandboxPrepareOperationResultEncoded =
  typeof SandboxPrepareOperationResultSchema.Encoded;
export type SandboxBeginEnsureRequest = typeof SandboxBeginEnsureRequestSchema.Type;
export type SandboxBeginEnsureRequestEncoded = typeof SandboxBeginEnsureRequestSchema.Encoded;
export type SandboxBeginExistingOperationRequest =
  typeof SandboxBeginExistingOperationRequestSchema.Type;
export type SandboxBeginExistingOperationRequestEncoded =
  typeof SandboxBeginExistingOperationRequestSchema.Encoded;
export type SandboxBeginOperationResult = typeof SandboxBeginOperationResultSchema.Type;
export type SandboxBeginOperationResultEncoded = typeof SandboxBeginOperationResultSchema.Encoded;
export type SandboxMetadataBackup = typeof SandboxMetadataBackupSchema.Type;
export type SandboxMetadataBackupEncoded = typeof SandboxMetadataBackupSchema.Encoded;
export type SandboxCompleteOperationRequest = typeof SandboxCompleteOperationRequestSchema.Type;
export type SandboxCompleteOperationRequestEncoded =
  typeof SandboxCompleteOperationRequestSchema.Encoded;
export type SandboxCompleteOperationResult = typeof SandboxCompleteOperationResultSchema.Type;
export type SandboxCompleteOperationResultEncoded =
  typeof SandboxCompleteOperationResultSchema.Encoded;
export type SandboxMetadataError = typeof SandboxMetadataErrorSchema.Type;
export type SandboxMetadataErrorEncoded = typeof SandboxMetadataErrorSchema.Encoded;
export type SandboxEnvironment = typeof SandboxEnvironmentSchema.Type;
export type SandboxEnvironmentEncoded = typeof SandboxEnvironmentSchema.Encoded;
export type SandboxEnvironmentName = typeof SandboxEnvironmentNameSchema.Type;
export type SandboxEnvironmentNameEncoded = typeof SandboxEnvironmentNameSchema.Encoded;
export type SandboxEnvironmentValue = typeof SandboxEnvironmentValueSchema.Type;
export type SandboxEnvironmentValueEncoded = typeof SandboxEnvironmentValueSchema.Encoded;
export type SandboxReference = typeof SandboxReferenceSchema.Type;
export type SandboxReferenceEncoded = typeof SandboxReferenceSchema.Encoded;
export type EnsureSandboxRequest = typeof EnsureSandboxRequestSchema.Type;
export type EnsureSandboxRequestEncoded = typeof EnsureSandboxRequestSchema.Encoded;
export type SandboxWorkerEnsureRequest = EnsureSandboxRequest & {
  readonly sandboxId: SandboxId;
  readonly executionGrant: SandboxEnsureExecutionGrant & { readonly sandboxId: SandboxId };
};
export type SandboxWorkerEnsureRequestEncoded = EnsureSandboxRequestEncoded & {
  readonly sandboxId: SandboxIdEncoded;
  readonly executionGrant: SandboxEnsureExecutionGrantEncoded & {
    readonly sandboxId: SandboxIdEncoded;
  };
};
export type SandboxCommandRequest = typeof SandboxCommandRequestSchema.Type;
export type SandboxCommandRequestEncoded = typeof SandboxCommandRequestSchema.Encoded;
export type SandboxCredentialProxyReference = typeof SandboxCredentialProxyReferenceSchema.Type;
export type SandboxCredentialProxyReferenceEncoded =
  typeof SandboxCredentialProxyReferenceSchema.Encoded;
export type SandboxProcessStopRequest = typeof SandboxProcessStopRequestSchema.Type;
export type SandboxProcessStopRequestEncoded = typeof SandboxProcessStopRequestSchema.Encoded;
export type SandboxTerminalRequest = typeof SandboxTerminalRequestSchema.Type;
export type SandboxTerminalRequestEncoded = typeof SandboxTerminalRequestSchema.Encoded;
export type SandboxEventStreamRequest = typeof SandboxEventStreamRequestSchema.Type;
export type SandboxEventStreamRequestEncoded = typeof SandboxEventStreamRequestSchema.Encoded;
export type SandboxListFilesRequest = typeof SandboxListFilesRequestSchema.Type;
export type SandboxListFilesRequestEncoded = typeof SandboxListFilesRequestSchema.Encoded;
export type SandboxReadTextFileRequest = typeof SandboxReadTextFileRequestSchema.Type;
export type SandboxReadTextFileRequestEncoded = typeof SandboxReadTextFileRequestSchema.Encoded;
export type SandboxWorkspaceFileOperation = typeof SandboxWorkspaceFileOperationSchema.Type;
export type SandboxAuthorizeWorkspaceFileReadRequest =
  typeof SandboxAuthorizeWorkspaceFileReadRequestSchema.Type;
export type SandboxDiscoverReadableRuntimesRequest =
  typeof SandboxDiscoverReadableRuntimesRequestSchema.Type;
export type SandboxDiscoverReadableRuntimesResult =
  typeof SandboxDiscoverReadableRuntimesResultSchema.Type;
export type SandboxReadableRuntime = typeof SandboxReadableRuntimeSchema.Type;
export type SandboxWorkspaceFileAuthority = typeof SandboxWorkspaceFileAuthoritySchema.Type;
export type SandboxAuthorizedListFilesRequest = typeof SandboxAuthorizedListFilesRequestSchema.Type;
export type SandboxAuthorizedReadTextFileRequest =
  typeof SandboxAuthorizedReadTextFileRequestSchema.Type;
export type SandboxWorkspaceFileExecutionGrant =
  typeof SandboxWorkspaceFileExecutionGrantSchema.Type;
export type SandboxWorkerListFilesRequest = typeof SandboxWorkerListFilesRequestSchema.Type;
export type SandboxWorkerListFilesRequestEncoded =
  typeof SandboxWorkerListFilesRequestSchema.Encoded;
export type SandboxWorkerReadTextFileRequest = typeof SandboxWorkerReadTextFileRequestSchema.Type;
export type SandboxWorkerReadTextFileRequestEncoded =
  typeof SandboxWorkerReadTextFileRequestSchema.Encoded;
export type SandboxWriteTextFileRequest = typeof SandboxWriteTextFileRequestSchema.Type;
export type SandboxWriteTextFileRequestEncoded = typeof SandboxWriteTextFileRequestSchema.Encoded;
export type SandboxPreviewRequest = typeof SandboxPreviewRequestSchema.Type;
export type SandboxPreviewRequestEncoded = typeof SandboxPreviewRequestSchema.Encoded;
export type SandboxPreviewAccess = typeof SandboxPreviewAccessSchema.Type;
export type SandboxPreviewAccessEncoded = typeof SandboxPreviewAccessSchema.Encoded;
export type SandboxCheckpointRequest = typeof SandboxCheckpointRequestSchema.Type;
export type SandboxCheckpointRequestEncoded = typeof SandboxCheckpointRequestSchema.Encoded;
export type SandboxRestoreRequest = typeof SandboxRestoreRequestSchema.Type;
export type SandboxRestoreRequestEncoded = typeof SandboxRestoreRequestSchema.Encoded;
export type SandboxDestroyRequest = typeof SandboxDestroyRequestSchema.Type;
export type SandboxDestroyRequestEncoded = typeof SandboxDestroyRequestSchema.Encoded;
export type SandboxProcess = typeof SandboxProcessSchema.Type;
export type SandboxProcessEncoded = typeof SandboxProcessSchema.Encoded;
export type SandboxCommandResult = typeof SandboxCommandResultSchema.Type;
export type SandboxCommandResultEncoded = typeof SandboxCommandResultSchema.Encoded;
export type SandboxRestoreResult = typeof SandboxRestoreResultSchema.Type;
export type SandboxRestoreResultEncoded = typeof SandboxRestoreResultSchema.Encoded;
export type SandboxDestroyResult = typeof SandboxDestroyResultSchema.Type;
export type SandboxDestroyResultEncoded = typeof SandboxDestroyResultSchema.Encoded;
export type SandboxFileEntry = typeof SandboxFileEntrySchema.Type;
export type SandboxFileEntryEncoded = typeof SandboxFileEntrySchema.Encoded;
export type SandboxListFilesResult = typeof SandboxListFilesResultSchema.Type;
export type SandboxListFilesResultEncoded = typeof SandboxListFilesResultSchema.Encoded;
export type SandboxReadTextFileResult = typeof SandboxReadTextFileResultSchema.Type;
export type SandboxReadTextFileResultEncoded = typeof SandboxReadTextFileResultSchema.Encoded;
export type SandboxWorkspaceFileErrorCode = typeof SandboxWorkspaceFileErrorCodeSchema.Type;
export type SandboxWorkspaceFileError = typeof SandboxWorkspaceFileErrorSchema.Type;
export type SandboxTerminalTicket = typeof SandboxTerminalTicketSchema.Type;
export type SandboxTerminalTicketEncoded = typeof SandboxTerminalTicketSchema.Encoded;
export type SandboxPreview = typeof SandboxPreviewSchema.Type;
export type SandboxPreviewEncoded = typeof SandboxPreviewSchema.Encoded;
export type SandboxBackup = typeof SandboxBackupSchema.Type;
export type SandboxBackupEncoded = typeof SandboxBackupSchema.Encoded;
export type ArtifactObject = typeof ArtifactObjectSchema.Type;
export type ArtifactObjectEncoded = typeof ArtifactObjectSchema.Encoded;
export type ArtifactObjectKind = typeof ArtifactObjectKindSchema.Type;
export type ArtifactObjectKindEncoded = typeof ArtifactObjectKindSchema.Encoded;
export type ArtifactObjectStatus = typeof ArtifactObjectStatusSchema.Type;
export type ArtifactObjectStatusEncoded = typeof ArtifactObjectStatusSchema.Encoded;
export type ArtifactRetention = typeof ArtifactRetentionSchema.Type;
export type ArtifactRetentionEncoded = typeof ArtifactRetentionSchema.Encoded;
export type UploadIntent = typeof UploadIntentSchema.Type;
export type UploadIntentEncoded = typeof UploadIntentSchema.Encoded;
export type MultipartPartTicket = typeof MultipartPartTicketSchema.Type;
export type MultipartPartTicketEncoded = typeof MultipartPartTicketSchema.Encoded;
export type MultipartCompletedPart = typeof MultipartCompletedPartSchema.Type;
export type MultipartCompletedPartEncoded = typeof MultipartCompletedPartSchema.Encoded;
export type CreateUploadIntentRequest = typeof CreateUploadIntentRequestSchema.Type;
export type CreateUploadIntentRequestEncoded = typeof CreateUploadIntentRequestSchema.Encoded;
export type MultipartPartTicketsRequest = typeof MultipartPartTicketsRequestSchema.Type;
export type MultipartPartTicketsRequestEncoded = typeof MultipartPartTicketsRequestSchema.Encoded;
export type CompleteMultipartUploadRequest = typeof CompleteMultipartUploadRequestSchema.Type;
export type CompleteMultipartUploadRequestEncoded =
  typeof CompleteMultipartUploadRequestSchema.Encoded;
export type AbortUploadRequest = typeof AbortUploadRequestSchema.Type;
export type AbortUploadRequestEncoded = typeof AbortUploadRequestSchema.Encoded;
export type FinalizeUploadRequest = typeof FinalizeUploadRequestSchema.Type;
export type FinalizeUploadRequestEncoded = typeof FinalizeUploadRequestSchema.Encoded;
export type ArtifactDownloadRequest = typeof ArtifactDownloadRequestSchema.Type;
export type ArtifactDownloadRequestEncoded = typeof ArtifactDownloadRequestSchema.Encoded;
export type DeleteArtifactObjectRequest = typeof DeleteArtifactObjectRequestSchema.Type;
export type DeleteArtifactObjectRequestEncoded = typeof DeleteArtifactObjectRequestSchema.Encoded;
export type SandboxRuntimeErrorCode = typeof SandboxRuntimeErrorCodeSchema.Type;
export type SandboxRuntimeErrorCodeEncoded = typeof SandboxRuntimeErrorCodeSchema.Encoded;
export type SandboxDurableRuntimeEvent = typeof SandboxDurableRuntimeEventSchema.Type;
export type SandboxDurableRuntimeEventEncoded = typeof SandboxDurableRuntimeEventSchema.Encoded;
export type SandboxTransientRuntimeEvent = typeof SandboxTransientRuntimeEventSchema.Type;
export type SandboxTransientRuntimeEventEncoded = typeof SandboxTransientRuntimeEventSchema.Encoded;
export type SandboxRuntimeEvent = typeof SandboxRuntimeEventSchema.Type;
export type SandboxRuntimeEventEncoded = typeof SandboxRuntimeEventSchema.Encoded;
